import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'en' | 'de' | 'fr' | 'ko';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Header
    'header.title': 'LogiSense Portal',
    'header.subtitle': 'European Logistics News & Risk Intelligence',
    'header.week': 'Week 45, 2025',
    
    // Navigation
    'nav.industry': 'Industry Highlights',
    'nav.risk': 'Risk Sensing',
    'nav.data': 'Logistics Data',
    
    // Industry Highlights
    'industry.title': 'WK 45 Industry Highlights',
    // CMA CGM Suez Article
    'article.cmacgm.title': 'CMA CGM Tests Waters with First Asia-Europe ULC Poised for Red Sea Transit',
    'article.cmacgm.date': 'November 4, 2025',
    'article.cmacgm.location': 'Suez Canal, Egypt',
    'article.cmacgm.badge': 'Maritime',
    'article.cmacgm.imageCaption': 'CMA CGM Benjamin Franklin approaching the Suez Canal',
    'article.cmacgm.summary': 'The 17,859 TEU CMA CGM Benjamin Franklin appears poised to enter the Suez Canal for its return journey to Asia, marking what would be the largest containership to use the vital waterway since early 2024.',
    'article.cmacgm.highlight': 'First alliance-operated Asia-Europe ship to transit the Red Sea in nearly two years',
    'article.cmacgm.section1.title': 'Historic Transit Attempt',
    'article.cmacgm.section1.content': 'The vessel is currently approaching Egypt\'s Port of Alexandria after departing Southampton, UK, on October 25. Its AIS destination is listed as Port Klang, Malaysia, representing a significant test of Red Sea shipping routes.',
    'article.cmacgm.section2.title': 'Red Sea Recovery Signs',
    'article.cmacgm.section2.content': 'According to Lloyd\'s List Intelligence data, containership traffic through the Bab el-Mandeb Strait has seen some increase this year compared with 2024, but remains at only a fraction of pre-crisis levels.',
    'article.cmacgm.section3.title': 'Security Considerations',
    'article.cmacgm.section3.content': 'CMA CGM vessels have reportedly been escorted by the French navy to shield them from Houthi drone attacks, with the carrier averaging about 16 transits a month this year.',
    'article.cmacgm.section4.title': 'Market Impact',
    'article.cmacgm.section4.content': 'The move comes after a ceasefire brokered by US President Donald Trump took effect on October 10, rekindling hopes for peace in Gaza and a gradual resumption of Red Sea sailings.',
    'article.cmacgm.impact.title': 'Industry Significance',
    'article.cmacgm.impact.content': 'This transit could signal the beginning of a broader fleet return to Red Sea routes, potentially reducing shipping costs and transit times for Asia-Europe trade.',

    // UPS Kuehne Article
    'article.upskuehne.title': 'UPS and Kuehne+Nagel Reduce Costs Amid Challenging Market Conditions',
    'article.upskuehne.date': 'November 3, 2025',
    'article.upskuehne.location': 'Global',
    'article.upskuehne.badge': 'Cost Reduction',
    'article.upskuehne.imageCaption': 'UPS and Kuehne+Nagel logistics operations',
    'article.upskuehne.summary': 'Major logistics companies UPS and Kuehne+Nagel are implementing significant cost reduction programs as they navigate challenging market conditions and declining profits in Q3 2025.',
    'article.upskuehne.highlight': 'Both companies focusing on structural cost reductions and facility rationalization',
    'article.upskuehne.section1.title': 'UPS Cost Reduction Initiative',
    'article.upskuehne.section1.content': 'UPS is experiencing a challenging market and continuing work on reducing structural costs, including a redundancy plan and rationalizing facilities to improve operational efficiency.',
    'article.upskuehne.section2.title': 'Kuehne+Nagel Response',
    'article.upskuehne.section2.content': 'Kuehne+Nagel has embarked on a group-wide cost reduction programme in the face of continued fall in profits in much of its business over the third quarter.',
    'article.upskuehne.section3.title': 'Industry-Wide Challenges',
    'article.upskuehne.section3.content': 'DSV is also navigating tough, volatile markets while integrating Schenker. Revenues have jumped but profitability has not risen by the same rate, suggesting even the biggest players are struggling.',
    'article.upskuehne.section4.title': 'Market Outlook',
    'article.upskuehne.section4.content': 'European logistics provider Logwin reported similar challenges, with subdued global growth and continued weakness in parts of the European economy, particularly in Germany.',
    'article.upskuehne.impact.title': 'Automotive Sector Impact',
    'article.upskuehne.impact.content': 'Key automotive logistics markets have suffered from structural shifts, with the transformation of the industry continuing at speed due to tariffs and the rise of Chinese EV manufacturers.',

    // Amazon Netherlands Article
    'article.amazon.title': 'Amazon to Invest More Than €1.4 Billion in the Netherlands',
    'article.amazon.date': 'November 4, 2025',
    'article.amazon.location': 'Netherlands',
    'article.amazon.badge': 'Investment',
    'article.amazon.imageCaption': 'Amazon logistics operations in the Netherlands',
    'article.amazon.summary': 'Amazon plans to invest more than €1.4 billion in the Netherlands over the next three years (2025-2027), representing the company\'s most significant financial commitment to the country since launching in 2020.',
    'article.amazon.highlight': '€1.4 billion investment over three years supporting AWS and retail operations',
    'article.amazon.section1.title': 'Investment Focus',
    'article.amazon.section1.content': 'The investment will support both Amazon Web Services (AWS) and retail business, focusing on infrastructure development and helping small and medium-sized businesses expand their reach.',
    'article.amazon.section2.title': 'Supporting Dutch Entrepreneurs',
    'article.amazon.section2.content': 'More than 60% of all products sold on Amazon.nl come from selling partners, with over 4,500 Dutch SMEs currently selling on Amazon, and nearly 90% successfully reaching customers worldwide.',
    'article.amazon.section3.title': 'Economic Impact',
    'article.amazon.section3.content': 'Amazon now employs over 1,000 people across the Netherlands. Independent research estimates that Amazon\'s investments supported more than 2,000 indirect jobs and 900 induced jobs in 2024.',
    'article.amazon.section4.title': 'Global Reach',
    'article.amazon.section4.content': 'Dutch SMEs on Amazon reach customers across 170 countries, with over 75% exporting to other EU member states. German, French, American, British, and Italian markets are top export destinations.',
    'article.amazon.impact.title': 'GDP Contribution',
    'article.amazon.impact.content': 'Amazon\'s investments contributed more than €200 million to the Netherlands\'s GDP in 2024, with a cumulative contribution exceeding €1 billion since 2013.',

    // Alibaba Liege Article
    'article.alibaba.title': 'Alibaba Cancels Liège Expansion Plans',
    'article.alibaba.date': 'November 5, 2025',
    'article.alibaba.location': 'Liège, Belgium',
    'article.alibaba.badge': 'Expansion Cancelled',
    'article.alibaba.imageCaption': 'Alibaba Cainiao logistics facility at Liège Airport',
    'article.alibaba.summary': 'Cainiao, Alibaba\'s logistics subsidiary, will not proceed with planned expansion at Liège Airport, cancelling two new warehouses that were on the drawing board.',
    'article.alibaba.highlight': 'Two additional warehouses measuring 24,000 and 20,000 square meters cancelled',
    'article.alibaba.section1.title': 'Strategic Review Decision',
    'article.alibaba.section1.content': 'The Chinese ecommerce giant informed Liège Airport authorities it will focus on operating existing infrastructure instead of expanding facilities.',
    'article.alibaba.section2.title': 'Existing Operations',
    'article.alibaba.section2.content': 'Cainiao operates a 30,000 square meter warehouse completed four years ago, which helped reduce AliExpress delivery times in Europe by 30 percent.',
    'article.alibaba.section3.title': 'Wallonia Impact',
    'article.alibaba.section3.content': 'The decision disappoints Wallonia\'s ambition to position itself as a logistics hub for Asian ecommerce companies.',
    'article.alibaba.section4.title': 'European Network',
    'article.alibaba.section4.content': 'Liège remains the centerpiece of Cainiao\'s European network, connecting air routes, China-Europe trains, and ocean freight.',
    'article.alibaba.impact.title': 'Future Plans',
    'article.alibaba.impact.content': 'It remains unclear whether Cainiao plans to open new warehouses elsewhere on the continent.',

    // InPost Iberia Article
    'article.inpost.title': 'InPost Strengthens Operations in Iberian Peninsula',
    'article.inpost.date': 'November 5, 2025',
    'article.inpost.location': 'Spain & Portugal',
    'article.inpost.badge': 'Network Expansion',
    'article.inpost.imageCaption': 'InPost parcel lockers across the Iberian Peninsula',
    'article.inpost.summary': 'InPost is increasing logistics capacity to handle up to 500,000 daily shipments during high season, representing a 220% rise compared to last year.',
    'article.inpost.highlight': 'Network expanded to almost 4,000 lockers with 55% available 24/7',
    'article.inpost.section1.title': 'Capacity Expansion',
    'article.inpost.section1.content': 'The company expanded its network throughout 2025, with 99.38% of lockers accessible at least six days a week.',
    'article.inpost.section2.title': 'Operational Improvements',
    'article.inpost.section2.content': 'InPost reinforced internal systems with a 60% increase in staff, adding 88 new hires and creating 13 permanent positions.',
    'article.inpost.section3.title': 'Service Coverage',
    'article.inpost.section3.content': 'Customers can choose between Locker, Punto Pack, or home delivery, covering 100% of postal codes in Spain, Portugal, and the islands.',
    'article.inpost.section4.title': 'International Integration',
    'article.inpost.section4.content': 'Following the acquisition of Sending in July, InPost now offers cross-border shipments from Belgium, France, Germany, Italy, Netherlands, and UK.',
    'article.inpost.impact.title': 'Peak Season Ready',
    'article.inpost.impact.content': 'The capacity boost prepares for Black Friday, Christmas, and post-holiday sales when parcel traffic reaches highest levels.',

    // EuroCommerce Tax Article
    'article.eurocommerce.title': 'EuroCommerce: Parcel Tax Should Be EU-Wide',
    'article.eurocommerce.date': 'November 5, 2025',
    'article.eurocommerce.location': 'European Union',
    'article.eurocommerce.badge': 'Policy Reform',
    'article.eurocommerce.imageCaption': 'European Union customs and parcel processing',
    'article.eurocommerce.summary': 'European retail association EuroCommerce urges policymakers to implement a unified €2 handling fee for international parcels to maintain single market integrity.',
    'article.eurocommerce.highlight': 'Unified €2 fee on packages from outside EU, with €0.50 for European warehouses',
    'article.eurocommerce.section1.title': 'European Commission Proposal',
    'article.eurocommerce.section1.content': 'The EC announced a fixed €2 fee on packages from outside the EU, paid by platforms, expected to take effect in March 2028.',
    'article.eurocommerce.section2.title': 'National Implementation Concerns',
    'article.eurocommerce.section2.content': 'Several countries want to implement parcel taxes earlier - Belgium plans a €2 fee starting 2026, Netherlands considering pre-2028 implementation.',
    'article.eurocommerce.section3.title': 'Fragmentation Risks',
    'article.eurocommerce.section3.content': 'EuroCommerce warns that national approaches could undermine single market integrity and create uneven playing fields.',
    'article.eurocommerce.section4.title': 'Market Impact',
    'article.eurocommerce.section4.content': 'Online consumer spending increased 7% in 2024, reaching €819 billion, making harmonized regulations crucial for continued growth.',
    'article.eurocommerce.impact.title': 'Harmonization Need',
    'article.eurocommerce.impact.content': 'A swift, harmonized EU solution is essential to prevent fragmentation and maintain level playing field across member states.',

    // COSCO Shipping Article
    'article.cosco.title': 'COSCO Shipping Revenues Fall Sharply in Q3 2025',
    'article.cosco.date': 'November 5, 2025',
    'article.cosco.location': 'Hong Kong',
    'article.cosco.badge': 'Revenue Decline',
    'article.cosco.imageCaption': 'COSCO container ship operations',
    'article.cosco.summary': 'China\'s flagship container line COSCO Shipping saw revenues fall sharply during Q3 on two main trades: Transpacific and Asia-Europe routes.',
    'article.cosco.highlight': 'Sharp revenue decline on Transpacific and Asia-Europe trade routes',
    'article.cosco.section1.title': 'Q3 2025 Performance',
    'article.cosco.section1.content': 'COSCO Shipping reported significant revenue declines in its third quarter filing to Hong Kong stock exchange.',
    'article.cosco.section2.title': 'Trade Route Impact',
    'article.cosco.section2.content': 'The Transpacific and Asia-Europe routes, two of COSCO\'s main trading corridors, experienced the most significant revenue drops.',
    'article.cosco.section3.title': 'Market Conditions',
    'article.cosco.section3.content': 'The revenue decline reflects challenging market conditions in the global container shipping industry.',
    'article.cosco.section4.title': 'Strategic Response',
    'article.cosco.section4.content': 'As China\'s flagship container line, COSCO\'s performance indicates broader trends in Asia-Europe and Transpacific trade.',
    'article.cosco.impact.title': 'Industry Implications',
    'article.cosco.impact.content': 'The revenue decline at COSCO signals potential challenges across the global container shipping sector.',

    // SF Express UK Article
    'article.sfexpress.title': 'SF Express Launches First UK Flights, Boosting East Midlands Airport',
    'article.sfexpress.date': 'November 4, 2025',
    'article.sfexpress.location': 'East Midlands, UK',
    'article.sfexpress.badge': 'New Routes',
    'article.sfexpress.imageCaption': 'SF Express cargo aircraft at East Midlands Airport',
    'article.sfexpress.summary': 'China\'s largest integrated logistics provider SF Express launched its first UK flights, connecting East Midlands Airport with Ezhou Huahu International Airport.',
    'article.sfexpress.highlight': 'First-ever UK flights connecting East Midlands with China\'s major logistics hub',
    'article.sfexpress.section1.title': 'Historic Launch',
    'article.sfexpress.section1.content': 'SF Express marked a significant milestone by launching its first UK flights, strengthening China-UK trade connections.',
    'article.sfexpress.section2.title': 'Strategic Hub Development',
    'article.sfexpress.section2.content': 'East Midlands Airport\'s growing reputation as a major international cargo hub receives significant boost from SF Express operations.',
    'article.sfexpress.section3.title': 'Enhanced Connectivity',
    'article.sfexpress.section3.content': 'The new route improves global connectivity and supports the region\'s role in the global supply chain.',
    'article.sfexpress.section4.title': 'Economic Impact',
    'article.sfexpress.section4.content': 'The expansion promises substantial growth to UK\'s tourism and hospitality sectors through increased international traffic.',
    'article.sfexpress.impact.title': 'Logistics Network Growth',
    'article.sfexpress.impact.content': 'Enhanced connectivity provides faster delivery services and broader range of travel options for businesses and travelers.',

    // China-EU Rail Article
    'article.railfreight.title': 'China-EU Rail Freight Down 27% in First Half of 2025',
    'article.railfreight.date': 'November 5, 2025',
    'article.railfreight.location': 'Eurasian Corridor',
    'article.railfreight.badge': 'Rail Decline',
    'article.railfreight.imageCaption': 'China-Europe rail freight operations',
    'article.railfreight.summary': 'China-EU rail freight volumes fell 27% to 138,009 TEUs in H1 2025, as shippers returned to maritime transport after Red Sea disruptions normalized.',
    'article.railfreight.highlight': 'Overall traffic down 27% with westbound falling 27.4% and eastbound 24.7%',
    'article.railfreight.section1.title': 'Traffic Decline',
    'article.railfreight.section1.content': 'Westbound rail traffic fell 27.4% to 118,291 TEUs, while eastbound decreased 24.7% to 19,718 TEUs in H1 2025.',
    'article.railfreight.section2.title': 'Asymmetrical Flows',
    'article.railfreight.section2.content': 'Flows remain largely asymmetrical, with volumes from China representing 86% of total traffic in H1 2025.',
    'article.railfreight.section3.title': 'Poland Gateway',
    'article.railfreight.section3.content': 'China-Poland corridor represents 93.4% of eastbound volumes, up from 82.5% in H1 2024, making Poland a structurally sound gateway.',
    'article.railfreight.section4.title': 'Maritime Return',
    'article.railfreight.section4.content': 'Shippers who used rail as Red Sea substitute have returned to maritime transport as rates became competitive again.',
    'article.railfreight.impact.title': 'Structural Challenges',
    'article.railfreight.impact.content': 'Rail freight struggles to maintain structural position in China-EU trade, with customers returning to maritime when disruptions normalize.',

    
    // Risk Sensing
    'risk.title': 'Latest Risk Sensing',
    
    'risk.cosco.title': 'COSCO Chartered Boxship Caught in China Port Fee Dispute',
    'risk.cosco.description': 'A COSCO-operated containership skipped China calls after becoming subject to new port fees targeting US-linked vessels, demonstrating strict enforcement even against state-owned companies.',
    'risk.cosco.date': 'October 28, 2025',
    'risk.cosco.badge': 'Trade Disruption',
    'risk.cosco.vessel': 'Vessel',
    'risk.cosco.status': 'Status',
    'risk.cosco.statusValue': 'Seeking Exemption',
    'risk.cosco.impact': 'Impact Level',
    'risk.cosco.impactValue': 'High Risk',
    
    // COSCO Boxship Article
    'article.coscoBoxship.title': 'COSCO Chartered Boxship Caught in China Port Fee Dispute',
    'article.coscoBoxship.readTime': '4 min read',
    'article.coscoBoxship.imageCaption': 'Container operations at Chinese port amid new fee regulations targeting US-linked vessels',
    'article.coscoBoxship.lead': 'A COSCO Shipping-operated containership has become an unexpected casualty of China\'s retaliatory port fees targeting US-linked vessels, highlighting the complex enforcement of new trade restrictions even against state-owned companies.',
    'article.coscoBoxship.section1.title': 'Vessel Caught in Policy Crossfire',
    'article.coscoBoxship.section1.content': 'The 2,550 TEU Halley, chartered by COSCO Shipping under a two-year deal since April 2025, was forced to skip Chinese port calls after authorities determined the vessel\'s US shareholding exceeded the 25% threshold set by China\'s Ministry of Transport.',
    'article.coscoBoxship.vessel.title': 'Vessel Specifications',
    'article.coscoBoxship.vessel.name': 'Vessel Name',
    'article.coscoBoxship.vessel.capacity': 'Capacity',
    'article.coscoBoxship.vessel.flag': 'Flag State',
    'article.coscoBoxship.vessel.charter': 'Charter Period',
    'article.coscoBoxship.vessel.charterValue': '2-year deal (April 2025)',
    'article.coscoBoxship.section2.title': 'Retaliatory Measures',
    'article.coscoBoxship.section2.content': 'The new port fees, implemented on October 14, 2025, were introduced in direct retaliation to US Trade Representative tariffs targeting China\'s maritime sector. The rules demonstrate Beijing\'s commitment to strict enforcement, with no exemptions even for Chinese state-owned operators.',
    'article.coscoBoxship.section3.title': 'Operational Challenges',
    'article.coscoBoxship.section3.content': 'Sources report the Liberia-flagged vessel waited off Ningbo for several days while COSCO sought an exemption from authorities. Alternative plans to divert to Hong Kong, which confirmed it would not apply the mainland fees, also made little progress.',
    'article.coscoBoxship.impact.title': 'Industry Impact Assessment',
    'article.coscoBoxship.impact.point1': 'Demonstrates strict enforcement of trade restrictions without favoritism',
    'article.coscoBoxship.impact.point2': 'Creates operational uncertainty for international shipping companies',
    'article.coscoBoxship.impact.point3': 'May affect other COSCO-chartered vessels with similar ownership structures',
    'article.coscoBoxship.impact.point4': 'Highlights complexity of US-China trade tensions in maritime sector',
    'article.coscoBoxship.section4.title': 'Broader Implications',
    'article.coscoBoxship.section4.content': 'Industry experts suggest this case may signal China\'s commitment to impartial enforcement of trade restrictions, potentially affecting more vessels in COSCO\'s chartered fleet. The incident underscores the growing complexity of navigating US-China trade tensions in the maritime sector.',
    'risk.strikes.title': 'Rotterdam and Antwerp Port Still Congested After End of Strikes',
    'risk.strikes.location': 'Netherlands & Belgium',
    'risk.strikes.content': 'While strikes have officially ended in both Rotterdam and Antwerp ports, significant congestion persists with vessel backlogs continuing to impact European logistics operations.',
    'risk.strikes.impact': 'Impact Assessment',
    'risk.strikes.ships': 'Ships Waiting (Peak):',
    'risk.strikes.duration': 'Strike Duration:',
    'risk.strikes.recovery': 'Recovery Status:',
    'risk.strikes.badge': 'Port Disruption',
    
    'risk.dashboard.title': 'Risk Monitoring Dashboard',
    'risk.dashboard.subtitle': 'Real-time logistics risk indicators',
    'risk.dashboard.congestion': 'Port Congestion',
    'risk.dashboard.volatility': 'Freight Rate Volatility',
    'risk.dashboard.rail': 'Rail Network Status',
    'risk.dashboard.regulatory': 'Regulatory Changes',
    
    // Logistics Data
    'data.title': 'Macroeconomic Index',
    'data.diesel.title': 'European Countries Diesel Fuel Prices',
    'data.diesel.subtitle': 'Monthly diesel prices per liter by country (EUR) - January 2024 to October 2025',
    'data.diesel.insights': 'Key Insights',
    'data.diesel.average': '• Average EU diesel price trend from €1.52 to €1.63/liter',
    'data.diesel.lowest': '• Lowest average was €1.51/liter in February 2024',
    'data.diesel.highest': '• Highest averages reached €1.63/liter in 2025',
    'data.diesel.change': '• Overall increase of €0.11/liter over 22-month period',
    
    'data.inflation.title': 'European Countries Inflation Rates',
    'data.inflation.subtitle': 'Annual inflation rates by country (%) - January 2024 to October 2025',
    'data.inflation.update': 'October 2025 Update',
    'data.inflation.current': 'Current Rate:',
    'data.inflation.target': 'ECB Target:',
    'data.inflation.monthly': 'Monthly Change:',
    'data.inflation.core': 'Core Inflation:',
    
    'data.minimumWage.title': 'European Countries Minimum Wages 2025',
    'data.minimumWage.subtitle': 'Actual minimum wage rates by country (EUR/hour) - 2025',
    'data.minimumWage.insights': 'Key Insights',
    'data.minimumWage.highest': '• Netherlands leads with €12.83/hour',
    'data.minimumWage.lowest': '• Poland has lowest rate at €4.30/hour',
    'data.minimumWage.average': '• Western Europe significantly higher than Eastern Europe',
    'data.minimumWage.stability': '• 3x wage difference between highest and lowest rates',
    
    // Common
    'common.share': 'Share',
    'common.back': 'Back',
    'common.readMore': 'Read More',
    'common.declining': 'Declining',
    'common.expansion': 'Expansion',
    'common.newPolicy': 'New Policy',
    'common.highRisk': 'High Risk',
    'common.high': 'High',
    'common.medium': 'Medium',
    'common.normal': 'Normal',
    'common.monitoring': 'Monitoring',
    'common.ongoing': 'Ongoing',
    
    // Articles
    'article.relatedArticles': 'Related Articles',
    
    // Freight Rates Article
    'article.freight.title': 'European Logistics Firms Face Impact from Falling Freight Rates',
    'article.freight.date': 'October 21, 2024',
    'article.freight.readTime': '5 min read',
    'article.freight.imageCaption': 'Container cranes at Bremerhaven port handling Maersk vessels amid declining freight rates',
    'article.freight.lead': 'European shipping and logistics companies are experiencing significant pressure as container rates drop to their lowest levels since January 2024, driven by industry oversupply and reduced demand following new U.S. trade policies.',
    'article.freight.section1.title': 'Market Pressures Mount',
    'article.freight.section1.content': 'The combination of overcapacity in the container shipping market and weakening global trade volumes has created a perfect storm for European logistics providers. Major carriers like Maersk are better positioned due to long-term contracts, but smaller operators face immediate margin pressure.',
    'article.freight.chart.title': 'Freight Rates & Volume Trends',
    'article.freight.chart.description': 'Monthly freight rates (USD) and container volumes (Million TEU)',
    'article.freight.section2.title': 'Regional Impact Analysis',
    'article.freight.section2.content': 'Northern European ports are experiencing the most significant impact, with Rotterdam and Hamburg seeing reduced throughput. The ripple effects extend to inland logistics networks, affecting rail and trucking operations across the continent.',
    'article.freight.keyStats.title': 'Key Market Indicators',
    'article.freight.keyStats.rateDecline': 'Rate decline since July',
    'article.freight.keyStats.currentRate': 'Current average rate (USD)',
    'article.freight.keyStats.volumeTEU': 'TEU processed (Oct)',
    'article.freight.keyStats.waitingShips': 'Ships awaiting berth',
    'article.freight.section3.title': 'Industry Response',
    'article.freight.section3.content': 'Logistics companies are implementing cost-cutting measures and exploring alternative revenue streams. Some are accelerating digitalization efforts to improve operational efficiency and reduce overhead costs.',
    'article.freight.section4.title': 'Outlook and Implications',
    'article.freight.section4.content': 'Analysts expect the current downturn to persist through Q1 2025, with recovery dependent on global trade normalization and capacity adjustments by major carriers. Companies with diversified service portfolios are likely to weather the storm better.',
    
    // Train Connection Article
    'article.train.title': 'FS Logistix Takes Over Train Connection Between Antwerp and Milan',
    'article.train.date': 'October 21, 2024',
    'article.train.readTime': '4 min read',
    'article.train.imageCaption': 'FS Logistix intermodal terminal at Antwerp Main Hub facilitating the new Milan connection',
    'article.train.lead': 'The Italian state-owned logistics company FS Logistix has successfully assumed control of the strategic Antwerp-Milan rail corridor, marking a significant expansion in European intermodal transport capabilities.',
    'article.train.section1.title': 'Strategic Acquisition Details',
    'article.train.section1.content': 'The takeover from Belgian operator Lineas represents a €45 million investment in European rail infrastructure. FS Logistix now operates through a partnership with Mercitalia Intermodal, utilizing Lineas locomotives under a comprehensive traction agreement.',
    'article.train.route.title': 'Route Specifications',
    'article.train.route.description': 'Complete journey details from Antwerp to Milan',
    'article.train.route.departure': 'Main Hub Terminal, Belgium',
    'article.train.route.arrival': 'Segrate Terminal, Italy',
    'article.train.route.terminalSize': 'Terminal area',
    'article.train.route.transitTime': 'Transit time',
    'article.train.section2.title': 'Environmental Impact',
    'article.train.section2.content': 'The service represents a major step toward sustainable logistics, removing thousands of trucks from European highways annually. Each train can carry the equivalent of 52 truck loads, significantly reducing carbon emissions and road congestion.',
    'article.train.environmental.title': 'Sustainability Metrics',
    'article.train.environmental.trucksRemoved': 'Trucks removed annually',
    'article.train.environmental.co2Reduction': 'CO2 emission reduction',
    'article.train.environmental.weeklyFrequency': 'Weekly departures',
    'article.train.section3.title': 'Operational Excellence',
    'article.train.section3.content': 'The Antwerp Main Hub terminal spans 200,000 square meters with eight 700-meter tracks, three gantry cranes, and six straddle carriers. This infrastructure supports efficient container handling and rapid turnaround times.',
    'article.train.section4.title': 'Future Expansion Plans',
    'article.train.section4.content': 'FS Logistix plans to extend connections to additional Italian destinations including Rome and Naples, while exploring opportunities for services to France, Germany, and Eastern Europe through the Antwerp hub.',
    'article.train.partnership.title': 'Partnership Structure',
    'article.train.partnership.fsRole': 'Service operator & commercial lead',
    'article.train.partnership.mercitaliaRole': 'Rail operations management',
    'article.train.partnership.lineasRole': 'Locomotive & traction services',
    
    // Carbon Market Article
    'article.carbon.title': 'Europe\'s New Carbon Market for Buildings and Road Transport',
    'article.carbon.date': 'October 21, 2024',
    'article.carbon.readTime': '6 min read',
    'article.carbon.imageCaption': 'European Commission building representing the new ETS2 carbon market legislation',
    'article.carbon.lead': 'The European Union\'s groundbreaking ETS2 system will revolutionize carbon pricing for buildings and transport sectors, creating the world\'s most comprehensive carbon market by 2027.',
    'article.carbon.section1.title': 'ETS2 Framework Overview',
    'article.carbon.section1.content': 'The new Emissions Trading System covers fuel distribution for buildings, road transport, and additional industrial sectors not included in the original EU ETS. This expansion represents a €104 billion market opportunity.',
    'article.carbon.chart.title': 'Sector Emission Targets',
    'article.carbon.chart.description': 'Current emissions vs 2030 targets by sector (Million tonnes CO2)',
    'article.carbon.chart.buildings': 'Buildings',
    'article.carbon.chart.transport': 'Transport',
    'article.carbon.chart.industry': 'Small Industry',
    'article.carbon.chart.current': 'Current Emissions',
    'article.carbon.chart.target2030': '2030 Target',
    'article.carbon.section2.title': 'Implementation Timeline',
    'article.carbon.section2.content': 'The system launches in 2027 with a price floor of €45 per tonne CO2. Member states will receive revenues to support green transition investments and protect vulnerable households from energy price increases.',
    'article.carbon.keyFeatures.title': 'Key Features',
    'article.carbon.keyFeatures.startDate': 'System launch year',
    'article.carbon.keyFeatures.reductionTarget': 'Emission reduction by 2030',
    'article.carbon.keyFeatures.priceFloor': 'Minimum carbon price',
    'article.carbon.keyFeatures.marketValue': 'Estimated market value',
    'article.carbon.section3.title': 'Market Mechanisms',
    'article.carbon.section3.content': 'The ETS2 includes automatic price stabilization mechanisms and revenue recycling to member states. A Market Stability Reserve will manage allowance supply to prevent excessive price volatility.',
    'article.carbon.timeline.title': 'Implementation Timeline',
    'article.carbon.timeline.preparation': 'System design & preparation phase',
    'article.carbon.timeline.launch': 'ETS2 becomes operational',
    'article.carbon.timeline.target': '42% emission reduction target',
    'article.carbon.section4.title': 'Economic Implications',
    'article.carbon.section4.content': 'The carbon market will generate significant revenues for green investments while incentivizing clean technology adoption. Industries are already preparing for the transition with efficiency improvements and alternative fuel strategies.',
    
    // AWS Outage Article
    'article.aws.title': 'AWS Outage Disrupts Global Supply Chains',
    'article.aws.date': 'October 25, 2024',
    'article.aws.readTime': '5 min read',
    'article.aws.imageCaption': 'Global cloud infrastructure disruption affecting supply chain operations',
    'article.aws.lead': 'A major Amazon Web Services outage lasting over 4 hours caused widespread disruptions to supply chain management systems across Europe and North America, highlighting the critical dependency on cloud infrastructure.',
    'article.aws.section1.title': 'Outage Impact Assessment',
    'article.aws.section1.content': 'The disruption affected AWS\'s US-East-1 region, cascading to European services and impacting logistics platforms, inventory management systems, and real-time tracking applications used by major retailers and logistics providers.',
    'article.aws.chart.title': 'Regional Service Recovery',
    'article.aws.chart.description': 'Service availability during and after the outage by region',
    'article.aws.chart.usEast': 'US East',
    'article.aws.chart.europe': 'Europe',
    'article.aws.chart.asiaPacific': 'Asia Pacific',
    'article.aws.chart.affected': 'Peak Impact',
    'article.aws.chart.recovered': 'Recovery Rate',
    'article.aws.section2.title': 'Supply Chain Disruptions',
    'article.aws.section2.content': 'E-commerce platforms experienced order processing delays, while logistics companies lost real-time visibility into shipment tracking. Warehouse management systems went offline, causing inventory discrepancies and fulfillment delays.',
    'article.aws.sectorChart.title': 'Sector Impact Distribution',
    'article.aws.sectorChart.description': 'Percentage of affected services by industry sector',
    'article.aws.sectors.ecommerce': 'E-commerce',
    'article.aws.sectors.logistics': 'Logistics',
    'article.aws.sectors.manufacturing': 'Manufacturing',
    'article.aws.sectors.retail': 'Retail',
    'article.aws.keyStats.title': 'Outage Statistics',
    'article.aws.keyStats.duration': 'Total outage duration',
    'article.aws.keyStats.estimatedLoss': 'Estimated economic impact',
    'article.aws.keyStats.affectedServices': 'Services affected',
    'article.aws.keyStats.recoveryRate': 'Full recovery achieved',
    'article.aws.section3.title': 'Recovery Operations',
    'article.aws.section3.content': 'AWS implemented emergency protocols to restore critical services, prioritizing supply chain and logistics applications. The company provided regular updates and extended service credits to affected customers.',
    'article.aws.timeline.title': 'Incident Timeline',
    'article.aws.timeline.start': 'Initial service degradation detected',
    'article.aws.timeline.peak': 'Peak impact across all regions',
    'article.aws.timeline.resolution': 'Full service restoration completed',
    'article.aws.section4.title': 'Industry Response',
    'article.aws.section4.content': 'The incident has prompted supply chain managers to reassess their cloud dependency and disaster recovery strategies. Many companies are now considering multi-cloud architectures to reduce single points of failure.',
    'article.aws.recommendations.title': 'Risk Mitigation Strategies',
    'article.aws.recommendations.multiCloud': 'Implement multi-cloud architecture',
    'article.aws.recommendations.backupSystems': 'Maintain offline backup systems',
    'article.aws.recommendations.monitoring': 'Enhanced monitoring and alerting',
    'article.aws.recommendations.contingency': 'Develop comprehensive contingency plans',
    
    // Port Strikes Article
    'article.strikes.title': 'Port Strikes Ease in Rotterdam and Antwerp, but Congestion Persists',
    'article.strikes.date': 'October 22, 2024',
    'article.strikes.readTime': '4 min read',
    'article.strikes.imageCaption': 'Container vessels queued outside Rotterdam port during the recent strikes',
    'article.strikes.lead': 'While industrial action has officially ended at Europe\'s two busiest ports, the aftermath continues to impact logistics operations with vessel backlogs and delayed cargo processing.',
    'article.strikes.section1.title': 'Strike Resolution',
    'article.strikes.section1.content': 'Both Rotterdam and Antwerp have reached agreements with striking workers, but the recovery process is expected to take several weeks. Port authorities are working around the clock to clear the backlog of waiting vessels.',
    'article.strikes.chart.title': 'Vessel Congestion Timeline',
    'article.strikes.chart.description': 'Number of ships waiting for berth allocation',
    'article.strikes.chart.oct5': 'Oct 5',
    'article.strikes.chart.oct8': 'Oct 8',
    'article.strikes.chart.oct10': 'Oct 10',
    'article.strikes.chart.oct15': 'Oct 15',
    'article.strikes.chart.oct18': 'Oct 18',
    'article.strikes.chart.oct22': 'Oct 22',
    'article.strikes.section2.title': 'Port-Specific Issues',
    'article.strikes.section2.content': 'Rotterdam faced lashing crew strikes over wage disputes, while Antwerp dealt with pilot strikes related to pension reforms. The different nature of these disputes required separate negotiation processes.',
    'article.strikes.rotterdam.issue': 'Strike Issue',
    'article.strikes.rotterdam.issueValue': 'Wage Disputes',
    'article.strikes.rotterdam.duration': 'Strike Duration',
    'article.strikes.rotterdam.peakShips': 'Peak Congestion',
    'article.strikes.antwerp.issue': 'Strike Issue',
    'article.strikes.antwerp.issueValue': 'Pension Reforms',
    'article.strikes.antwerp.duration': 'Strike Duration',
    'article.strikes.antwerp.peakShips': 'Peak Congestion',
    'article.strikes.section3.title': 'Economic Impact',
    'article.strikes.section3.content': 'The combined strikes resulted in significant economic losses, with delayed shipments affecting supply chains across Europe. Retailers and manufacturers faced inventory shortages and increased logistics costs.',
    'article.strikes.economicImpact.title': 'Financial Impact Assessment',
    'article.strikes.economicImpact.dailyLoss': 'Daily economic loss',
    'article.strikes.economicImpact.affectedTEU': 'TEU affected',
    'article.strikes.economicImpact.delayIncrease': 'Average delay increase',
    'article.strikes.economicImpact.avgDelay': 'Average processing delay',
    'article.strikes.section4.title': 'Recovery Progress',
    'article.strikes.section4.content': 'Port operations are gradually returning to normal, with priority given to time-sensitive cargo and perishable goods. Additional staff and extended operating hours are being implemented to accelerate recovery.',
    'article.strikes.recovery.title': 'Current Recovery Status',
    'article.strikes.recovery.rotterdam': 'Rotterdam Operations',
    'article.strikes.recovery.rotterdamStatus': 'Fully Operational',
    'article.strikes.recovery.antwerp': 'Antwerp Operations',
    'article.strikes.recovery.antwerpStatus': 'Recovering',
    'article.strikes.recovery.normalOps': 'Return to Normal',
    
    // Source Section
    'article.source.title': 'Original Source',
    'article.source.description': 'Read the original article from the source publication:',
    'article.source.readOriginal': 'Read Original Article',
    'article.source.publisher': 'Publisher',
    
    // Logistics Partners Insights
    // Market Insights
    'market.title': 'Market Insights',
    'market.subtitle': 'Monthly Logistics Insights Report - November 2025',
    'market.executiveSummary.title': 'Executive Summary',
    'market.roadFreight.title': 'European Road Freight Market',
    'market.roadFreight.summary': 'Rates have stabilized at elevated levels after Q2 peaks, with spot and contract rates converging. However, capacity remains critically tight, exacerbated by a severe driver shortage of over 426,000 positions across Europe.',
    'market.roadFreight.keyPoint1': 'Driver shortage: 426,000+ unfilled positions',
    'market.roadFreight.keyPoint2': 'Rates stabilized at index level 132.2-134.06',
    'market.roadFreight.keyPoint3': '15.4% fall in new EU truck registrations',
    'market.oceanFreight.title': 'Global Ocean Freight Market',
    'market.oceanFreight.summary': 'Ocean freight rates are declining from their H1 2025 highs, as front-loaded volumes to mitigate tariff uncertainty have subsided, leading to softening demand.',
    'market.oceanFreight.keyPoint1': 'SCFI composite at 1,550.70 points (Oct 31)',
    'market.oceanFreight.keyPoint2': 'Global reliability at 65.2% in September',
    'market.oceanFreight.keyPoint3': 'Average delay for late vessels: 4.88 days',
    'market.airFreight.title': 'Global Air Freight Market',
    'market.airFreight.summary': 'The air freight market shows stable demand against a backdrop of improving capacity, particularly on European routes benefiting from summer passenger schedules.',
    'market.airFreight.keyPoint1': 'CTKs rose 2.9% year-over-year in September',
    'market.airFreight.keyPoint2': 'Capacity (ACTKs) grew 3.0% in parallel',
    'market.airFreight.keyPoint3': 'ICS2 and SAF mandates increasing costs',
    'market.realEstate.title': 'European Real Estate & Warehousing',
    'market.realEstate.summary': 'The European logistics real estate market is experiencing a slowdown aligned with fragile economic recovery. While investor interest remains for prime locations with high-quality tenants, speculative development has decreased.',
    'market.realEstate.keyPoint1': 'Warehouse take-up at lowest level since 2014',
    'market.realEstate.keyPoint2': 'Prime yields stable at 4.50% in Germany',
    'market.realEstate.keyPoint3': 'Spain showing resilience as outperformer',
    'market.recommendations.title': 'Immediate Actions for Shippers',
    'market.recommendations.point1': 'Diversify carrier mix for stable and spot capacity',
    'market.recommendations.point2': 'Implement pre-pulls and drop trailers',
    'market.recommendations.point3': 'Enhance slotting flexibility with off-peak hours',
    'market.recommendations.point4': 'Add 7-12 days buffer for Asia-Europe shipments',

    'partners.title': 'Logistics Partners Insights',
    'partners.kuehne.title': 'Europe Must Invest in Port Infrastructure for Strategic Resilience',
    'partners.kuehne.subtitle': 'Maersk CEO and APMT Leadership Call for Unified European Port Strategy',
    'partners.kuehne.badge': 'November 2025',
    'partners.kuehne.keyTakeaways': 'Key Messages',
    'partners.kuehne.additionalInsights': 'Strategic Implications',
    'partners.kuehne.takeaway1': 'Europe needs serious investment in port infrastructure to relieve congestion and build strategic resilience',
    'partners.kuehne.takeaway2': 'Port capacity is scarce across the continent, requiring unified approach to infrastructure development',
    'partners.kuehne.takeaway3': 'Investment must keep pace with trade growth, efficiency demands, and changing geopolitical realities',
    'partners.kuehne.takeaway4': 'Chinese influence in major European ports raises strategic concerns for EU sovereignty',
    'partners.kuehne.takeaway5': 'Northern European major ports face persistent congestion throughout 2025',
    'partners.kuehne.takeaway6': 'Tsunami of tonnage expected in coming years will strain existing port capacity',
    'partners.kuehne.takeaway7': 'Legacy infrastructure not built for next generation of larger, more numerous ships',
    'partners.kuehne.takeaway8': 'Ports should be viewed as critical utilities like airports, roads, and water systems',
    'partners.kuehne.source': 'Kuehne+Nagel Maritime Intelligence',
    'partners.kuehne.sourceDescription': 'Strategic analysis of European port infrastructure and capacity challenges',
    'partners.kuehne.viewTracker': 'Read Full Analysis',
    // Footer
    'footer.title': 'LogiSense Portal',
    'footer.subtitle': 'European Logistics News & Risk Intelligence',
    'footer.updated': 'Last updated: November 7, 2025 | Data sources: IRU, Ti Insight, Shanghai Shipping Exchange, Drewry Maritime, Sea-Intelligence, IATA, European Commission, CBRE, Cushman & Wakefield',
    
    // Chart labels
    'chart.rate': 'Rate (USD)',
    'chart.volume': 'Volume (M TEU)',
    'chart.inflationRate': 'Inflation Rate',
    'chart.pricePerLiter': 'Price per Liter',
    
    // Countries
    'country.germany': 'Germany',
    'country.france': 'France',
    'country.netherlands': 'Netherlands',
    'country.belgium': 'Belgium',
    'country.italy': 'Italy',
    
    // Months
    'month.jan': 'Jan',
    'month.feb': 'Feb',
    'month.mar': 'Mar',
    'month.apr': 'Apr',
    'month.may': 'May',
    'month.jun': 'Jun',
    'month.jul': 'Jul',
    'month.aug': 'Aug',
    'month.sep': 'Sep',
    'month.oct': 'Oct',
    'month.nov': 'Nov',
    'month.dec': 'Dec',
  },
  de: {
    // Header
    'header.title': 'LogiSense Portal',
    'header.subtitle': 'Europäische Logistiknachrichten & Risikointelligenz',
    'header.week': 'Woche 45, 2025',
    
    // Navigation
    'nav.industry': 'Branchenhighlights',
    'nav.risk': 'Risikoerkennung',
    'nav.data': 'Logistikdaten',
    
    // Industry Highlights
    'industry.title': 'KW 45 Branchenhighlights',
    'industry.freight.title': 'Europäische Logistikunternehmen leiden unter fallenden Frachtraten',
    'industry.freight.date': '15. Oktober 2025',
    'industry.freight.content': 'Europäische Schifffahrts- und Logistikunternehmen stehen unter Druck, da die Containerraten auf den niedrigsten Stand seit Januar 2024 fallen, verursacht durch Überangebot und schwächere Nachfrage nach neuen US-Zöllen.',
    'industry.freight.chart': 'Frachtratenverlauf',
    'industry.freight.badge': 'Frachtraten',
    
    'industry.train.title': 'FS Logistix übernimmt Zugverbindung zwischen Antwerpen und Mailand',
    'industry.train.date': '18. Oktober 2025',
    'industry.train.content': 'Italiens FS Logistix hat den intermodalen Zugservice Antwerpen-Mailand übernommen, der fünfmal wöchentlich verkehrt und jährlich etwa 13.000 LKW von europäischen Straßen entfernt.',
    'industry.train.connections': 'Wöchentliche Verbindungen:',
    'industry.train.trucks': 'Entfernte LKW:',
    'industry.train.route': 'Route:',
    'industry.train.badge': 'Schienenverkehr',
    
    'industry.carbon.title': 'Europas neuer Kohlenstoffmarkt für Gebäude und Straßenverkehr',
    'industry.carbon.date': '22. Oktober 2025',
    'industry.carbon.content': 'Das neue Emissionshandelssystem (ETS2) der EU wird ab 2027 CO2-Emissionen aus Gebäuden und Straßenverkehr abdecken und bestehende Klimapolitiken ergänzen, um die Klimaneutralitätsziele 2050 zu erreichen.',
    'industry.carbon.launch': 'Startdatum:',
    'industry.carbon.reduction': 'Emissionsreduktion:',
    'industry.carbon.sectors': 'Sektoren:',
    // CMA CGM Suez Article
    'article.cmacgm.title': 'CMA CGM testet Gewässer mit erstem Asien-Europa ULC für Rotes Meer Transit',
    'article.cmacgm.date': '4. November 2025',
    'article.cmacgm.location': 'Suezkanal, Ägypten',
    'article.cmacgm.badge': 'Schifffahrt',
    'article.cmacgm.imageCaption': 'CMA CGM Benjamin Franklin nähert sich dem Suezkanal',
    'article.cmacgm.summary': 'Die 17.859 TEU CMA CGM Benjamin Franklin scheint bereit zu sein, den Suezkanal für ihre Rückreise nach Asien zu befahren und wäre damit das größte Containerschiff, das die wichtige Wasserstraße seit Anfang 2024 nutzt.',
    'article.cmacgm.highlight': 'Erstes allianz-betriebenes Asien-Europa Schiff für Rotes Meer Transit seit fast zwei Jahren',
    'article.cmacgm.section1.title': 'Historischer Transitversuch',
    'article.cmacgm.section1.content': 'Das Schiff nähert sich derzeit Ägyptens Hafen Alexandria nach der Abfahrt von Southampton, UK, am 25. Oktober. Sein AIS-Ziel ist Port Klang, Malaysia.',
    'article.cmacgm.section2.title': 'Zeichen der Rotes Meer Erholung',
    'article.cmacgm.section2.content': 'Laut Lloyd\'s List Intelligence Daten hat der Containerschiffsverkehr durch die Bab el-Mandeb Straße dieses Jahr eine gewisse Zunahme verzeichnet, bleibt aber nur bei einem Bruchteil der Vor-Krisen-Niveaus.',
    'article.cmacgm.section3.title': 'Sicherheitsüberlegungen',
    'article.cmacgm.section3.content': 'CMA CGM Schiffe wurden Berichten zufolge von der französischen Marine eskortiert, um sie vor Houthi-Drohnenangriffen zu schützen.',
    'article.cmacgm.section4.title': 'Marktauswirkungen',
    'article.cmacgm.section4.content': 'Der Schritt erfolgt nach einem von US-Präsident Donald Trump vermittelten Waffenstillstand, der am 10. Oktober in Kraft trat.',
    'article.cmacgm.impact.title': 'Branchenbedeutung',
    'article.cmacgm.impact.content': 'Dieser Transit könnte den Beginn einer breiteren Flottenrückkehr zu Rotes Meer Routen signalisieren.',

    // UPS Kuehne Article
    'article.upskuehne.title': 'UPS und Kuehne+Nagel reduzieren Kosten bei schwierigen Marktbedingungen',
    'article.upskuehne.date': '3. November 2025',
    'article.upskuehne.location': 'Global',
    'article.upskuehne.badge': 'Kostenreduzierung',
    'article.upskuehne.imageCaption': 'UPS und Kuehne+Nagel Logistikoperationen',
    'article.upskuehne.summary': 'Große Logistikunternehmen UPS und Kuehne+Nagel implementieren bedeutende Kostenreduzierungsprogramme während sie schwierige Marktbedingungen navigieren.',
    'article.upskuehne.highlight': 'Beide Unternehmen fokussieren auf strukturelle Kostenreduzierungen und Anlagenrationalisierung',
    'article.upskuehne.section1.title': 'UPS Kostenreduzierungsinitiative',
    'article.upskuehne.section1.content': 'UPS erlebt einen herausfordernden Markt und setzt die Arbeit zur Reduzierung struktureller Kosten fort, einschließlich eines Entlassungsplans.',
    'article.upskuehne.section2.title': 'Kuehne+Nagel Antwort',
    'article.upskuehne.section2.content': 'Kuehne+Nagel hat ein gruppenweites Kostenreduzierungsprogramm angesichts anhaltender Gewinnrückgänge eingeleitet.',
    'article.upskuehne.section3.title': 'Branchenweite Herausforderungen',
    'article.upskuehne.section3.content': 'DSV navigiert ebenfalls schwierige, volatile Märkte während der Integration von Schenker.',
    'article.upskuehne.section4.title': 'Marktausblick',
    'article.upskuehne.section4.content': 'Europäischer Logistikanbieter Logwin berichtete ähnliche Herausforderungen mit gedämpftem globalem Wachstum.',
    'article.upskuehne.impact.title': 'Automobilsektor Auswirkungen',
    'article.upskuehne.impact.content': 'Wichtige Automobillogistikmärkte haben unter strukturellen Verschiebungen gelitten.',

    // Amazon Netherlands Article
    'article.amazon.title': 'Amazon investiert mehr als 1,4 Milliarden Euro in die Niederlande',
    'article.amazon.date': '4. November 2025',
    'article.amazon.location': 'Niederlande',
    'article.amazon.badge': 'Investition',
    'article.amazon.imageCaption': 'Amazon Logistikoperationen in den Niederlanden',
    'article.amazon.summary': 'Amazon plant, mehr als 1,4 Milliarden Euro in die Niederlande über die nächsten drei Jahre (2025-2027) zu investieren.',
    'article.amazon.highlight': '1,4 Milliarden Euro Investition über drei Jahre zur Unterstützung von AWS und Einzelhandelsoperationen',
    'article.amazon.section1.title': 'Investitionsfokus',
    'article.amazon.section1.content': 'Die Investition wird sowohl Amazon Web Services (AWS) als auch das Einzelhandelsgeschäft unterstützen.',
    'article.amazon.section2.title': 'Unterstützung niederländischer Unternehmer',
    'article.amazon.section2.content': 'Mehr als 60% aller auf Amazon.nl verkauften Produkte stammen von Verkaufspartnern.',
    'article.amazon.section3.title': 'Wirtschaftliche Auswirkungen',
    'article.amazon.section3.content': 'Amazon beschäftigt jetzt über 1.000 Menschen in den Niederlanden.',
    'article.amazon.section4.title': 'Globale Reichweite',
    'article.amazon.section4.content': 'Niederländische KMU auf Amazon erreichen Kunden in 170 Ländern.',
    'article.amazon.impact.title': 'BIP-Beitrag',
    'article.amazon.impact.content': 'Amazons Investitionen trugen mehr als 200 Millionen Euro zum niederländischen BIP in 2024 bei.',

    // Alibaba Liege Article
    'article.alibaba.title': 'Alibaba bricht Liège Erweiterungspläne ab',
    'article.alibaba.date': '5. November 2025',
    'article.alibaba.location': 'Liège, Belgien',
    'article.alibaba.badge': 'Erweiterung abgebrochen',
    'article.alibaba.imageCaption': 'Alibaba Cainiao Logistikanlage am Flughafen Liège',
    'article.alibaba.summary': 'Cainiao, Alibabas Logistiktochter, wird die geplante Erweiterung am Flughafen Liège nicht fortsetzen.',
    'article.alibaba.highlight': 'Zwei zusätzliche Lagerhäuser mit 24.000 und 20.000 Quadratmetern abgebrochen',
    'article.alibaba.section1.title': 'Strategische Überprüfung',
    'article.alibaba.section1.content': 'Der chinesische E-Commerce-Riese wird sich auf bestehende Infrastruktur konzentrieren.',
    'article.alibaba.section2.title': 'Bestehende Operationen',
    'article.alibaba.section2.content': 'Cainiao betreibt ein 30.000 m² Lager, das AliExpress-Lieferzeiten um 30% reduzierte.',
    'article.alibaba.section3.title': 'Wallonien Auswirkungen',
    'article.alibaba.section3.content': 'Die Entscheidung enttäuscht Walloniens Logistikzentrum-Ambitionen.',
    'article.alibaba.section4.title': 'Europäisches Netzwerk',
    'article.alibaba.section4.content': 'Liège bleibt Herzstück von Cainaos europäischem Netzwerk.',
    'article.alibaba.impact.title': 'Zukunftspläne',
    'article.alibaba.impact.content': 'Unklar ob neue Lagerhäuser anderswo geplant sind.',

    // InPost Iberia Article
    'article.inpost.title': 'InPost stärkt Operationen auf Iberischer Halbinsel',
    'article.inpost.date': '5. November 2025',
    'article.inpost.location': 'Spanien & Portugal',
    'article.inpost.badge': 'Netzwerkerweiterung',
    'article.inpost.imageCaption': 'InPost Paketschließfächer auf der Iberischen Halbinsel',
    'article.inpost.summary': 'InPost erhöht Kapazität auf 500.000 tägliche Sendungen, 220% Anstieg gegenüber Vorjahr.',
    'article.inpost.highlight': 'Netzwerk auf fast 4.000 Schließfächer erweitert, 55% rund um die Uhr',
    'article.inpost.section1.title': 'Kapazitätserweiterung',
    'article.inpost.section1.content': 'Netzwerk 2025 erweitert, 99,38% der Schließfächer mindestens 6 Tage zugänglich.',
    'article.inpost.section2.title': 'Betriebsverbesserungen',
    'article.inpost.section2.content': '60% Personalaufstockung, 88 Neueinstellungen, 13 feste Stellen.',
    'article.inpost.section3.title': 'Service-Abdeckung',
    'article.inpost.section3.content': '100% Postleitzahlen-Abdeckung in Spanien, Portugal und Inseln.',
    'article.inpost.section4.title': 'Internationale Integration',
    'article.inpost.section4.content': 'Grenzüberschreitende Sendungen aus 6 europäischen Ländern nach Sending-Übernahme.',
    'article.inpost.impact.title': 'Hochsaison-bereit',
    'article.inpost.impact.content': 'Vorbereitung auf Black Friday, Weihnachten und Nachfeiertagsverkauf.',

    // EuroCommerce Tax Article
    'article.eurocommerce.title': 'EuroCommerce: Paketsteuer sollte EU-weit sein',
    'article.eurocommerce.date': '5. November 2025',
    'article.eurocommerce.location': 'Europäische Union',
    'article.eurocommerce.badge': 'Politikreform',
    'article.eurocommerce.imageCaption': 'EU Zoll und Paketbearbeitung',
    'article.eurocommerce.summary': 'EuroCommerce drängt auf einheitliche 2-Euro-Gebühr für internationale Pakete.',
    'article.eurocommerce.highlight': '2-Euro-Gebühr für Pakete von außerhalb der EU, 0,50 Euro für EU-Lager',
    'article.eurocommerce.section1.title': 'EU-Kommissions-Vorschlag',
    'article.eurocommerce.section1.content': 'Feste 2-Euro-Gebühr von Plattformen bezahlt, März 2028 Inkrafttreten.',
    'article.eurocommerce.section2.title': 'Nationale Umsetzung',
    'article.eurocommerce.section2.content': 'Belgien plant 2-Euro-Gebühr ab 2026, Niederlande vor 2028.',
    'article.eurocommerce.section3.title': 'Fragmentierungsrisiken',
    'article.eurocommerce.section3.content': 'Nationale Ansätze könnten Binnenmarkt-Integrität untergraben.',
    'article.eurocommerce.section4.title': 'Marktauswirkungen',
    'article.eurocommerce.section4.content': 'Online-Ausgaben stiegen 2024 um 7% auf 819 Milliarden Euro.',
    'article.eurocommerce.impact.title': 'Harmonisierungsbedarf',
    'article.eurocommerce.impact.content': 'Schnelle EU-Lösung nötig für gleiche Wettbewerbsbedingungen.',

    
    // Risk Sensing
    'risk.title': 'Aktuelle Risikoerkennung',
    
    'risk.cosco.title': 'COSCO-gecharterte Containerschiff in chinesischem Hafengebühren-Streit gefangen',
    'risk.cosco.description': 'Ein COSCO-betriebenes Containerschiff übersprang China-Anläufe nach neuen Hafengebühren für US-verknüpfte Schiffe, was strenge Durchsetzung auch gegen Staatsunternehmen zeigt.',
    'risk.cosco.date': '28. Oktober 2025',
    'risk.cosco.badge': 'Handelsstörung',
    'risk.cosco.vessel': 'Schiff',
    'risk.cosco.status': 'Status',
    'risk.cosco.statusValue': 'Sucht Ausnahme',
    'risk.cosco.impact': 'Auswirkungsgrad',
    'risk.cosco.impactValue': 'Hohes Risiko',
    
    // COSCO Boxship Article
    'article.cosco.title': 'COSCO-gecharterte Containerschiff in chinesischem Hafengebühren-Streit gefangen',
    'article.cosco.readTime': '4 Min. Lesezeit',
    'article.cosco.imageCaption': 'Containeroperationen im chinesischen Hafen inmitten neuer Gebührenvorschriften für US-verknüpfte Schiffe',
    'article.cosco.lead': 'Ein von COSCO Shipping betriebenes Containerschiff ist zu einem unerwarteten Opfer von Chinas Vergeltungsgebühren gegen US-verknüpfte Schiffe geworden und verdeutlicht die komplexe Durchsetzung neuer Handelsbeschränkungen auch gegen Staatsunternehmen.',
    'article.cosco.section1.title': 'Schiff im politischen Kreuzfeuer gefangen',
    'article.cosco.section1.content': 'Die 2.550 TEU Halley, die seit April 2025 unter einem Zweijahresvertrag von COSCO Shipping gechartert wurde, war gezwungen, chinesische Hafenanlaufstellen zu überspringen, nachdem die Behörden feststellten, dass die US-Beteiligung des Schiffes die 25%-Schwelle des chinesischen Verkehrsministeriums überschritt.',
    'article.cosco.vessel.title': 'Schiffsspezifikationen',
    'article.cosco.vessel.name': 'Schiffsname',
    'article.cosco.vessel.capacity': 'Kapazität',
    'article.cosco.vessel.flag': 'Flaggenstaat',
    'article.cosco.vessel.charter': 'Charterperiode',
    'article.cosco.vessel.charterValue': '2-Jahres-Vertrag (April 2025)',
    'article.cosco.section2.title': 'Vergeltungsmaßnahmen',
    'article.cosco.section2.content': 'Die am 14. Oktober 2025 eingeführten neuen Hafengebühren wurden als direkte Vergeltung für die Zölle des US-Handelsbeauftragten gegen Chinas Schifffahrtssektor eingeführt. Die Regeln demonstrieren Pekings Engagement für strikte Durchsetzung ohne Ausnahmen selbst für chinesische staatliche Betreiber.',
    'article.cosco.section3.title': 'Betriebliche Herausforderungen',
    'article.cosco.section3.content': 'Quellen berichten, dass das unter liberianischer Flagge fahrende Schiff mehrere Tage vor Ningbo wartete, während COSCO eine Ausnahme von den Behörden suchte. Alternative Pläne zur Umleitung nach Hongkong, das bestätigte, die Festlandgebühren nicht anzuwenden, machten ebenfalls wenig Fortschritte.',
    'article.cosco.impact.title': 'Branchenauswirkungsbewertung',
    'article.cosco.impact.point1': 'Zeigt strikte Durchsetzung von Handelsbeschränkungen ohne Bevorzugung',
    'article.cosco.impact.point2': 'Schafft operative Unsicherheit für internationale Reedereien',
    'article.cosco.impact.point3': 'Könnte andere COSCO-gecharterte Schiffe mit ähnlichen Eigentumsstrukturen betreffen',
    'article.cosco.impact.point4': 'Verdeutlicht die Komplexität der US-China-Handelsspannungen im Schifffahrtssektor',
    'article.cosco.section4.title': 'Breitere Auswirkungen',
    'article.cosco.section4.content': 'Branchenexperten vermuten, dass dieser Fall Chinas Engagement für unparteiische Durchsetzung von Handelsbeschränkungen signalisieren könnte und möglicherweise mehr Schiffe in COSCOs gecharteter Flotte betrifft. Der Vorfall unterstreicht die wachsende Komplexität der Navigation von US-China-Handelsspannungen im Schifffahrtssektor.',
    'risk.strikes.title': 'Häfen Rotterdam und Antwerpen nach Streikende weiterhin überlastet',
    'risk.strikes.location': 'Niederlande & Belgien',
    'risk.strikes.content': 'Obwohl die Streiks in den Häfen Rotterdam und Antwerpen offiziell beendet sind, bestehen erhebliche Staus fort, da Schiffsrückstände weiterhin die europäischen Logistikoperationen beeinträchtigen.',
    'risk.strikes.impact': 'Auswirkungsbewertung',
    'risk.strikes.ships': 'Wartende Schiffe (Spitze):',
    'risk.strikes.duration': 'Streikdauer:',
    'risk.strikes.recovery': 'Erholungsstatus:',
    'risk.strikes.badge': 'Hafenstörung',
    
    'risk.dashboard.title': 'Risiko-Monitoring-Dashboard',
    'risk.dashboard.subtitle': 'Echtzeit-Logistikrisikoindikatoren',
    'risk.dashboard.congestion': 'Hafenstau',
    'risk.dashboard.volatility': 'Frachtratenschwankungen',
    'risk.dashboard.rail': 'Schienennetzstatus',
    'risk.dashboard.regulatory': 'Regulatorische Änderungen',
    
    // Logistics Data
    'data.title': 'Makroökonomischer Index',
    'data.diesel.title': 'Europäische Länder Dieselkraftstoffpreise',
    'data.diesel.subtitle': 'Monatliche Dieselpreise pro Liter nach Ländern (EUR) - Januar 2024 bis Oktober 2025',
    'data.diesel.insights': 'Wichtige Erkenntnisse',
    'data.diesel.average': '• Durchschnittlicher EU-Dieselpreis-Trend von €1,52 bis €1,63/Liter',
    'data.diesel.lowest': '• Niedrigster Durchschnitt war €1,51/Liter im Februar 2024',
    'data.diesel.highest': '• Höchste Durchschnitte erreichten €1,63/Liter in 2025',
    'data.diesel.change': '• Gesamtanstieg von €0,11/Liter über 22-Monats-Zeitraum',
    
    'data.inflation.title': 'Europäische Länder Inflationsraten',
    'data.inflation.subtitle': 'Jährliche Inflationsraten nach Ländern (%) - Januar 2024 bis Oktober 2025',
    'data.inflation.update': 'Oktober 2025 Update',
    'data.inflation.current': 'Aktuelle Rate:',
    'data.inflation.target': 'EZB-Ziel:',
    'data.inflation.monthly': 'Monatliche Änderung:',
    'data.inflation.core': 'Kerninflation:',
    
    'data.minimumWage.title': 'Europäische Länder Mindestlöhne 2025',
    'data.minimumWage.subtitle': 'Tatsächliche Mindestlohnsätze nach Ländern (EUR/Stunde) - 2025',
    'data.minimumWage.insights': 'Wichtige Erkenntnisse',
    'data.minimumWage.highest': '• Niederlande führen mit €12,83/Stunde',
    'data.minimumWage.lowest': '• Polen hat niedrigsten Satz mit €4,30/Stunde',
    'data.minimumWage.average': '• Westeuropa deutlich höher als Osteuropa',
    'data.minimumWage.stability': '• 3-facher Lohnunterschied zwischen höchstem und niedrigstem Satz',
    
    // Common
    'common.share': 'Teilen',
    'common.back': 'Zurück',
    'common.readMore': 'Mehr lesen',
    'common.declining': 'Rückläufig',
    'common.expansion': 'Expansion',
    'common.newPolicy': 'Neue Politik',
    'common.highRisk': 'Hohes Risiko',
    'common.high': 'Hoch',
    'common.medium': 'Mittel',
    'common.normal': 'Normal',
    'common.monitoring': 'Überwachung',
    'common.ongoing': 'Laufend',
    
    // Articles
    'article.relatedArticles': 'Verwandte Artikel',
    
    // Freight Rates Article
    'article.freight.title': 'Europäische Logistikunternehmen leiden unter fallenden Frachtraten',
    'article.freight.date': '21. Oktober 2024',
    'article.freight.readTime': '5 Min. Lesezeit',
    'article.freight.imageCaption': 'Containerkräne im Hafen Bremerhaven beim Umschlag von Maersk-Schiffen bei sinkenden Frachtraten',
    'article.freight.lead': 'Europäische Schifffahrts- und Logistikunternehmen stehen unter erheblichem Druck, da die Containerraten auf den niedrigsten Stand seit Januar 2024 fallen, verursacht durch Überangebot in der Branche und reduzierte Nachfrage nach neuen US-Handelspolitiken.',
    'article.freight.section1.title': 'Marktdruck steigt',
    'article.freight.section1.content': 'Die Kombination aus Überkapazitäten im Containerschifffahrtsmarkt und schwächelnden globalen Handelsvolumen hat einen perfekten Sturm für europäische Logistikanbieter geschaffen. Große Reedereien wie Maersk sind durch langfristige Verträge besser positioniert, aber kleinere Betreiber stehen unter unmittelbarem Margendruck.',
    'article.freight.chart.title': 'Frachtraten & Volumentrends',
    'article.freight.chart.description': 'Monatliche Frachtraten (USD) und Containervolumen (Millionen TEU)',
    'article.freight.section2.title': 'Regionale Auswirkungsanalyse',
    'article.freight.section2.content': 'Nordeuropäische Häfen erleben die stärksten Auswirkungen, wobei Rotterdam und Hamburg reduzierten Durchsatz verzeichnen. Die Auswirkungen erstrecken sich auf Binnenlogistiknetzwerke und betreffen Schienen- und LKW-Operationen in ganz Europa.',
    'article.freight.keyStats.title': 'Wichtige Marktindikatoren',
    'article.freight.keyStats.rateDecline': 'Ratenrückgang seit Juli',
    'article.freight.keyStats.currentRate': 'Aktueller Durchschnittssatz (USD)',
    'article.freight.keyStats.volumeTEU': 'TEU verarbeitet (Okt)',
    'article.freight.keyStats.waitingShips': 'Schiffe warten auf Liegeplatz',
    'article.freight.section3.title': 'Branchenreaktion',
    'article.freight.section3.content': 'Logistikunternehmen implementieren Kostensenkungsmaßnahmen und erkunden alternative Einnahmequellen. Einige beschleunigen Digitalisierungsbemühungen zur Verbesserung der Betriebseffizienz und Reduzierung der Gemeinkosten.',
    'article.freight.section4.title': 'Ausblick und Auswirkungen',
    'article.freight.section4.content': 'Analysten erwarten, dass der aktuelle Abschwung bis Q1 2025 anhält, wobei die Erholung von der Normalisierung des globalen Handels und Kapazitätsanpassungen großer Reedereien abhängt. Unternehmen mit diversifizierten Serviceportfolios werden den Sturm wahrscheinlich besser überstehen.',
    
    // Train Connection Article
    'article.train.title': 'FS Logistix übernimmt Zugverbindung zwischen Antwerpen und Mailand',
    'article.train.date': '21. Oktober 2024',
    'article.train.readTime': '4 Min. Lesezeit',
    'article.train.imageCaption': 'FS Logistix Intermodalterminal am Antwerpen Main Hub für die neue Mailand-Verbindung',
    'article.train.lead': 'Das italienische staatliche Logistikunternehmen FS Logistix hat erfolgreich die Kontrolle über den strategischen Schienenkorridor Antwerpen-Mailand übernommen und markiert eine bedeutende Expansion der europäischen intermodalen Transportkapazitäten.',
    'article.train.section1.title': 'Details der strategischen Übernahme',
    'article.train.section1.content': 'Die Übernahme vom belgischen Betreiber Lineas stellt eine 45-Millionen-Euro-Investition in die europäische Schieneninfrastruktur dar. FS Logistix operiert nun durch eine Partnerschaft mit Mercitalia Intermodal und nutzt Lineas-Lokomotiven unter einem umfassenden Traktionsvertrag.',
    'article.train.route.title': 'Streckendaten',
    'article.train.route.description': 'Vollständige Reisedetails von Antwerpen nach Mailand',
    'article.train.route.departure': 'Main Hub Terminal, Belgien',
    'article.train.route.arrival': 'Segrate Terminal, Italien',
    'article.train.route.terminalSize': 'Terminalfläche',
    'article.train.route.transitTime': 'Transitzeit',
    'article.train.section2.title': 'Umweltauswirkungen',
    'article.train.section2.content': 'Der Service stellt einen wichtigen Schritt in Richtung nachhaltiger Logistik dar und entfernt jährlich Tausende von LKW von europäischen Autobahnen. Jeder Zug kann das Äquivalent von 52 LKW-Ladungen transportieren und reduziert damit erheblich CO2-Emissionen und Straßenstaus.',
    'article.train.environmental.title': 'Nachhaltigkeitskennzahlen',
    'article.train.environmental.trucksRemoved': 'LKW jährlich entfernt',
    'article.train.environmental.co2Reduction': 'CO2-Emissionsreduktion',
    'article.train.environmental.weeklyFrequency': 'Wöchentliche Abfahrten',
    'article.train.section3.title': 'Operative Exzellenz',
    'article.train.section3.content': 'Das Antwerpen Main Hub Terminal erstreckt sich über 200.000 Quadratmeter mit acht 700-Meter-Gleisen, drei Portalkränen und sechs Straddle Carriern. Diese Infrastruktur unterstützt effizienten Containerumschlag und schnelle Umschlagszeiten.',
    'article.train.section4.title': 'Zukünftige Expansionspläne',
    'article.train.section4.content': 'FS Logistix plant, Verbindungen zu zusätzlichen italienischen Zielen einschließlich Rom und Neapel zu erweitern, während Möglichkeiten für Services nach Frankreich, Deutschland und Osteuropa über den Antwerpen-Hub erkundet werden.',
    'article.train.partnership.title': 'Partnerschaftsstruktur',
    'article.train.partnership.fsRole': 'Service-Betreiber & kommerzielle Führung',
    'article.train.partnership.mercitaliaRole': 'Schienenoperationsmanagement',
    'article.train.partnership.lineasRole': 'Lokomotiv- & Traktionsservices',
    
    // Carbon Market Article
    'article.carbon.title': 'Europas neuer Kohlenstoffmarkt für Gebäude und Straßenverkehr',
    'article.carbon.date': '21. Oktober 2024',
    'article.carbon.readTime': '6 Min. Lesezeit',
    'article.carbon.imageCaption': 'Gebäude der Europäischen Kommission als Symbol für die neue ETS2-Kohlenstoffmarktgesetzgebung',
    'article.carbon.lead': 'Das bahnbrechende ETS2-System der Europäischen Union wird die Kohlenstoffpreisgestaltung für Gebäude- und Verkehrssektoren revolutionieren und bis 2027 den weltweit umfassendsten Kohlenstoffmarkt schaffen.',
    'article.carbon.section1.title': 'ETS2-Rahmenübersicht',
    'article.carbon.section1.content': 'Das neue Emissionshandelssystem umfasst die Kraftstoffverteilung für Gebäude, Straßenverkehr und zusätzliche Industriesektoren, die nicht im ursprünglichen EU-ETS enthalten sind. Diese Erweiterung stellt eine 104-Milliarden-Euro-Marktchance dar.',
    'article.carbon.chart.title': 'Sektorale Emissionsziele',
    'article.carbon.chart.description': 'Aktuelle Emissionen vs. 2030-Ziele nach Sektor (Millionen Tonnen CO2)',
    'article.carbon.chart.buildings': 'Gebäude',
    'article.carbon.chart.transport': 'Verkehr',
    'article.carbon.chart.industry': 'Kleinindustrie',
    'article.carbon.chart.current': 'Aktuelle Emissionen',
    'article.carbon.chart.target2030': '2030-Ziel',
    'article.carbon.section2.title': 'Umsetzungszeitplan',
    'article.carbon.section2.content': 'Das System startet 2027 mit einem Mindestpreis von 45 Euro pro Tonne CO2. Mitgliedstaaten erhalten Einnahmen zur Unterstützung grüner Übergangsinvestitionen und zum Schutz vulnerabler Haushalte vor Energiepreissteigerungen.',
    'article.carbon.keyFeatures.title': 'Hauptmerkmale',
    'article.carbon.keyFeatures.startDate': 'Systemstartjahr',
    'article.carbon.keyFeatures.reductionTarget': 'Emissionsreduktion bis 2030',
    'article.carbon.keyFeatures.priceFloor': 'Mindest-Kohlenstoffpreis',
    'article.carbon.keyFeatures.marketValue': 'Geschätzter Marktwert',
    'article.carbon.section3.title': 'Marktmechanismen',
    'article.carbon.section3.content': 'Das ETS2 umfasst automatische Preisstabilisierungsmechanismen und Einnahmenrecycling an Mitgliedstaaten. Eine Marktstabilitätsreserve wird das Zertifikatangebot verwalten, um übermäßige Preisvolatilität zu verhindern.',
    'article.carbon.timeline.title': 'Umsetzungszeitplan',
    'article.carbon.timeline.preparation': 'Systemdesign & Vorbereitungsphase',
    'article.carbon.timeline.launch': 'ETS2 wird operativ',
    'article.carbon.timeline.target': '42% Emissionsreduktionsziel',
    'article.carbon.section4.title': 'Wirtschaftliche Auswirkungen',
    'article.carbon.section4.content': 'Der Kohlenstoffmarkt wird erhebliche Einnahmen für grüne Investitionen generieren und gleichzeitig die Einführung sauberer Technologien fördern. Industrien bereiten sich bereits auf den Übergang mit Effizienzverbesserungen und alternativen Kraftstoffstrategien vor.',
    
    // AWS Outage Article
    'article.aws.title': 'AWS-Ausfall stört globale Lieferketten',
    'article.aws.date': '25. Oktober 2024',
    'article.aws.readTime': '5 Min. Lesezeit',
    'article.aws.imageCaption': 'Globale Cloud-Infrastrukturstörung betrifft Lieferkettenoperationen',
    'article.aws.lead': 'Ein großer Amazon Web Services-Ausfall von über 4 Stunden verursachte weit verbreitete Störungen in Lieferkettenmanagementsystemen in Europa und Nordamerika und verdeutlichte die kritische Abhängigkeit von Cloud-Infrastruktur.',
    'article.aws.section1.title': 'Ausfallauswirkungsbewertung',
    'article.aws.section1.content': 'Die Störung betraf AWS\' US-East-1-Region, kaskadierte zu europäischen Services und beeinträchtigte Logistikplattformen, Bestandsmanagementsysteme und Echtzeit-Tracking-Anwendungen großer Einzelhändler und Logistikanbieter.',
    'article.aws.chart.title': 'Regionale Service-Wiederherstellung',
    'article.aws.chart.description': 'Service-Verfügbarkeit während und nach dem Ausfall nach Region',
    'article.aws.chart.usEast': 'US Ost',
    'article.aws.chart.europe': 'Europa',
    'article.aws.chart.asiaPacific': 'Asien-Pazifik',
    'article.aws.chart.affected': 'Spitzenauswirkung',
    'article.aws.chart.recovered': 'Wiederherstellungsrate',
    'article.aws.section2.title': 'Lieferkettenstörungen',
    'article.aws.section2.content': 'E-Commerce-Plattformen erlebten Bestellverarbeitungsverzögerungen, während Logistikunternehmen die Echtzeit-Sichtbarkeit in die Sendungsverfolgung verloren. Lagerverwaltungssysteme gingen offline und verursachten Bestandsdiskrepanzen und Erfüllungsverzögerungen.',
    'article.aws.sectorChart.title': 'Sektorale Auswirkungsverteilung',
    'article.aws.sectorChart.description': 'Prozentsatz betroffener Services nach Industriesektor',
    'article.aws.sectors.ecommerce': 'E-Commerce',
    'article.aws.sectors.logistics': 'Logistik',
    'article.aws.sectors.manufacturing': 'Fertigung',
    'article.aws.sectors.retail': 'Einzelhandel',
    'article.aws.keyStats.title': 'Ausfallstatistiken',
    'article.aws.keyStats.duration': 'Gesamtausfalldauer',
    'article.aws.keyStats.estimatedLoss': 'Geschätzte wirtschaftliche Auswirkung',
    'article.aws.keyStats.affectedServices': 'Betroffene Services',
    'article.aws.keyStats.recoveryRate': 'Vollständige Wiederherstellung erreicht',
    'article.aws.section3.title': 'Wiederherstellungsoperationen',
    'article.aws.section3.content': 'AWS implementierte Notfallprotokolle zur Wiederherstellung kritischer Services und priorisierte Lieferketten- und Logistikanwendungen. Das Unternehmen stellte regelmäßige Updates bereit und gewährte Service-Credits an betroffene Kunden.',
    'article.aws.timeline.title': 'Vorfallzeitplan',
    'article.aws.timeline.start': 'Erste Service-Verschlechterung erkannt',
    'article.aws.timeline.peak': 'Spitzenauswirkung in allen Regionen',
    'article.aws.timeline.resolution': 'Vollständige Service-Wiederherstellung abgeschlossen',
    'article.aws.section4.title': 'Branchenreaktion',
    'article.aws.section4.content': 'Der Vorfall hat Lieferkettenmanager dazu veranlasst, ihre Cloud-Abhängigkeit und Disaster-Recovery-Strategien zu überdenken. Viele Unternehmen erwägen nun Multi-Cloud-Architekturen zur Reduzierung einzelner Ausfallpunkte.',
    'article.aws.recommendations.title': 'Risikominderungsstrategien',
    'article.aws.recommendations.multiCloud': 'Multi-Cloud-Architektur implementieren',
    'article.aws.recommendations.backupSystems': 'Offline-Backup-Systeme aufrechterhalten',
    'article.aws.recommendations.monitoring': 'Erweiterte Überwachung und Alarmierung',
    'article.aws.recommendations.contingency': 'Umfassende Notfallpläne entwickeln',
    
    // Port Strikes Article
    'article.strikes.title': 'Hafenstreiks in Rotterdam und Antwerpen lassen nach, aber Staus bleiben bestehen',
    'article.strikes.date': '22. Oktober 2024',
    'article.strikes.readTime': '4 Min. Lesezeit',
    'article.strikes.imageCaption': 'Containerschiffe in der Warteschlange vor dem Hafen Rotterdam während der jüngsten Streiks',
    'article.strikes.lead': 'Während die Arbeitskämpfe in Europas zwei verkehrsreichsten Häfen offiziell beendet sind, wirken sich die Nachwirkungen weiterhin auf Logistikoperationen mit Schiffsrückständen und verzögerter Frachtabfertigung aus.',
    'article.strikes.section1.title': 'Streiklösung',
    'article.strikes.section1.content': 'Sowohl Rotterdam als auch Antwerpen haben Vereinbarungen mit streikenden Arbeitern erreicht, aber der Erholungsprozess wird voraussichtlich mehrere Wochen dauern. Hafenbehörden arbeiten rund um die Uhr, um den Rückstau wartender Schiffe zu beseitigen.',
    'article.strikes.chart.title': 'Schiffsstau-Zeitplan',
    'article.strikes.chart.description': 'Anzahl der Schiffe, die auf Liegeplätze warten',
    'article.strikes.chart.oct5': '5. Okt',
    'article.strikes.chart.oct8': '8. Okt',
    'article.strikes.chart.oct10': '10. Okt',
    'article.strikes.chart.oct15': '15. Okt',
    'article.strikes.chart.oct18': '18. Okt',
    'article.strikes.chart.oct22': '22. Okt',
    'article.strikes.section2.title': 'Hafenspezifische Probleme',
    'article.strikes.section2.content': 'Rotterdam sah sich Streiks der Zurr-Crews wegen Lohnstreitigkeiten gegenüber, während Antwerpen mit Lotsenstreiks im Zusammenhang mit Rentenreformen zu kämpfen hatte. Die unterschiedliche Natur dieser Streitigkeiten erforderte separate Verhandlungsprozesse.',
    'article.strikes.rotterdam.issue': 'Streikproblem',
    'article.strikes.rotterdam.issueValue': 'Lohnstreitigkeiten',
    'article.strikes.rotterdam.duration': 'Streikdauer',
    'article.strikes.rotterdam.peakShips': 'Spitzenstau',
    'article.strikes.antwerp.issue': 'Streikproblem',
    'article.strikes.antwerp.issueValue': 'Rentenreformen',
    'article.strikes.antwerp.duration': 'Streikdauer',
    'article.strikes.antwerp.peakShips': 'Spitzenstau',
    'article.strikes.section3.title': 'Wirtschaftliche Auswirkungen',
    'article.strikes.section3.content': 'Die kombinierten Streiks führten zu erheblichen wirtschaftlichen Verlusten, wobei verzögerte Sendungen Lieferketten in ganz Europa betrafen. Einzelhändler und Hersteller sahen sich Bestandsengpässen und erhöhten Logistikkosten gegenüber.',
    'article.strikes.economicImpact.title': 'Finanzielle Auswirkungsbewertung',
    'article.strikes.economicImpact.dailyLoss': 'Täglicher wirtschaftlicher Verlust',
    'article.strikes.economicImpact.affectedTEU': 'Betroffene TEU',
    'article.strikes.economicImpact.delayIncrease': 'Durchschnittliche Verzögerungszunahme',
    'article.strikes.economicImpact.avgDelay': 'Durchschnittliche Verarbeitungsverzögerung',
    'article.strikes.section4.title': 'Erholungsfortschritt',
    'article.strikes.section4.content': 'Hafenoperationen kehren allmählich zur Normalität zurück, wobei zeitkritische Fracht und verderbliche Güter Priorität haben. Zusätzliches Personal und erweiterte Betriebszeiten werden implementiert, um die Erholung zu beschleunigen.',
    'article.strikes.recovery.title': 'Aktueller Erholungsstatus',
    'article.strikes.recovery.rotterdam': 'Rotterdam-Operationen',
    'article.strikes.recovery.rotterdamStatus': 'Voll betriebsbereit',
    'article.strikes.recovery.antwerp': 'Antwerpen-Operationen',
    'article.strikes.recovery.antwerpStatus': 'In Erholung',
    'article.strikes.recovery.normalOps': 'Rückkehr zur Normalität',
    
    // Source Section
    'article.source.title': 'Originalquelle',
    'article.source.description': 'Lesen Sie den Originalartikel aus der Quellpublikation:',
    'article.source.readOriginal': 'Originalartikel lesen',
    'article.source.publisher': 'Herausgeber',
    
    // Logistics Partners Insights
    // Market Insights
    'market.title': 'Markteinblicke',
    'market.subtitle': 'Monatlicher Logistik-Einblicke Bericht - November 2025',
    'market.executiveSummary.title': 'Zusammenfassung',
    'market.roadFreight.title': 'Europäischer Straßengüterverkehr',
    'market.roadFreight.summary': 'Die Raten haben sich nach den Q2-Spitzen auf erhöhtem Niveau stabilisiert, wobei Spot- und Vertragsraten konvergieren. Die Kapazität bleibt jedoch kritisch knapp, verschärft durch einen schweren Fahrermangel von über 426.000 Stellen in Europa.',
    'market.roadFreight.keyPoint1': 'Fahrermangel: 426.000+ unbesetzte Stellen',
    'market.roadFreight.keyPoint2': 'Raten stabilisiert bei Index 132.2-134.06',
    'market.roadFreight.keyPoint3': '15.4% Rückgang bei EU-Lkw-Neuzulassungen',
    'market.oceanFreight.title': 'Globaler Seefracht-Markt',
    'market.oceanFreight.summary': 'Seefrachtpreise fallen von ihren H1 2025-Höchstständen, da vorgezogene Volumen zur Minderung der Zollunsicherheit nachgelassen haben, was zu einer Nachfrageabschwächung führt.',
    'market.oceanFreight.keyPoint1': 'SCFI-Composite bei 1.550,70 Punkten (31. Okt)',
    'market.oceanFreight.keyPoint2': 'Globale Zuverlässigkeit bei 65,2% im September',
    'market.oceanFreight.keyPoint3': 'Durchschnittliche Verspätung: 4,88 Tage',
    'market.airFreight.title': 'Globaler Luftfracht-Markt',
    'market.airFreight.summary': 'Der Luftfrachtmarkt zeigt stabile Nachfrage vor dem Hintergrund verbesserter Kapazität, insbesondere auf europäischen Strecken, die von Sommer-Passagierflugplänen profitieren.',
    'market.airFreight.keyPoint1': 'CTKs stiegen um 2,9% im Jahresvergleich im September',
    'market.airFreight.keyPoint2': 'Kapazität (ACTKs) wuchs parallel um 3,0%',
    'market.airFreight.keyPoint3': 'ICS2- und SAF-Mandate erhöhen Kosten',
    'market.realEstate.title': 'Europäische Immobilien & Lagerhaltung',
    'market.realEstate.summary': 'Der europäische Logistikimmobilienmarkt erlebt eine Verlangsamung im Einklang mit der fragilen wirtschaftlichen Erholung. Während Investoreninteresse für erstklassige Standorte mit hochwertigen Mietern besteht, hat die spekulative Entwicklung abgenommen.',
    'market.realEstate.keyPoint1': 'Lageraufnahme auf niedrigstem Niveau seit 2014',
    'market.realEstate.keyPoint2': 'Prime-Renditen stabil bei 4,50% in Deutschland',
    'market.realEstate.keyPoint3': 'Spanien zeigt Widerstandsfähigkeit als Outperformer',
    'market.recommendations.title': 'Sofortige Maßnahmen für Versender',
    'market.recommendations.point1': 'Diversifizierung des Carrier-Mix für stabile und Spot-Kapazität',
    'market.recommendations.point2': 'Implementierung von Pre-Pulls und Drop-Trailern',
    'market.recommendations.point3': 'Verbesserung der Slot-Flexibilität mit Off-Peak-Zeiten',
    'market.recommendations.point4': '7-12 Tage Puffer für Asien-Europa-Sendungen hinzufügen',

    'partners.title': 'Logistikpartner Einblicke',
    'partners.kuehne.title': 'Europa muss in Hafeninfrastruktur für strategische Resilienz investieren',
    'partners.kuehne.subtitle': 'Maersk CEO und APMT Führung fordern einheitliche europäische Hafenstrategie',
    'partners.kuehne.badge': 'November 2025',
    'partners.kuehne.keyTakeaways': 'Kernbotschaften',
    'partners.kuehne.additionalInsights': 'Strategische Implikationen',
    'partners.kuehne.takeaway1': 'Europa braucht ernsthafte Investitionen in Hafeninfrastruktur zur Stauabbau und strategischen Resilienz',
    'partners.kuehne.takeaway2': 'Hafenkapazität ist kontinentweit knapp und erfordert einheitlichen Infrastrukturansatz',
    'partners.kuehne.takeaway3': 'Investitionen müssen mit Handelswachstum, Effizienzanforderungen und geopolitischen Realitäten Schritt halten',
    'partners.kuehne.takeaway4': 'Chinesischer Einfluss in großen europäischen Häfen wirft strategische Bedenken für EU-Souveränität auf',
    'partners.kuehne.takeaway5': 'Große nordeuropäische Häfen leiden 2025 unter anhaltenden Staus',
    'partners.kuehne.takeaway6': 'Tsunami von Tonnage in kommenden Jahren wird bestehende Hafenkapazität belasten',
    'partners.kuehne.takeaway7': 'Legacy-Infrastruktur nicht für nächste Generation größerer, zahlreicherer Schiffe gebaut',
    'partners.kuehne.takeaway8': 'Häfen sollten als kritische Versorgungseinrichtungen wie Flughäfen, Straßen und Wassersysteme betrachtet werden',
    'partners.kuehne.source': 'Kuehne+Nagel Maritime Intelligence',
    'partners.kuehne.sourceDescription': 'Strategische Analyse europäischer Hafeninfrastruktur und Kapazitätsherausforderungen',
    'partners.kuehne.viewTracker': 'Vollständige Analyse lesen',
    // Footer
    'footer.title': 'LogiSense Portal',
    'footer.subtitle': 'Europäische Logistiknachrichten & Risikointelligenz',
    'footer.updated': 'Zuletzt aktualisiert: 7. November 2025 | Datenquellen: IRU, Ti Insight, Shanghai Shipping Exchange, Drewry Maritime, Sea-Intelligence, IATA, Europäische Kommission, CBRE, Cushman & Wakefield',
    
    // Chart labels
    'chart.rate': 'Rate (USD)',
    'chart.inflationRate': 'Inflationsrate',
    'chart.pricePerLiter': 'Preis pro Liter',
    
    // Countries
    'country.germany': 'Deutschland',
    'country.france': 'Frankreich',
    'country.netherlands': 'Niederlande',
    'country.belgium': 'Belgien',
    'country.italy': 'Italien',
    
    // Months
    'month.jan': 'Jan',
    'month.feb': 'Feb',
    'month.mar': 'Mär',
    'month.apr': 'Apr',
    'month.may': 'Mai',
    'month.jun': 'Jun',
    'month.jul': 'Jul',
    'month.aug': 'Aug',
    'month.sep': 'Sep',
    'month.oct': 'Okt',
    'month.nov': 'Nov',
    'month.dec': 'Dez',
  },
  fr: {
    // Header
    'header.title': 'LogiSense Portal',
    'header.subtitle': 'Actualités Logistiques Européennes & Intelligence des Risques',
    'header.week': 'Semaine 45, 2025',
    
    // Navigation
    'nav.industry': 'Points Forts de l\'Industrie',
    'nav.risk': 'Détection des Risques',
    'nav.data': 'Données Logistiques',
    
    // Industry Highlights
    'industry.title': 'Points Forts de l\'Industrie S45',
    'industry.freight.title': 'Les Entreprises Logistiques Européennes Subissent l\'Impact de la Chute des Taux de Fret',
    'industry.freight.date': '15 octobre 2025',
    'industry.freight.content': 'Les entreprises de transport maritime et de logistique européennes sont sous pression alors que les taux de conteneurs océaniques chutent à leur plus bas niveau depuis janvier 2024, causé par la suroffre industrielle et une demande plus faible suite aux nouveaux tarifs américains.',
    'industry.freight.chart': 'Tendance des Taux de Fret',
    'industry.freight.badge': 'Taux de Fret',
    
    'industry.train.title': 'FS Logistix Reprend la Connexion Ferroviaire entre Anvers et Milan',
    'industry.train.date': '18 octobre 2025',
    'industry.train.content': 'FS Logistix d\'Italie a repris le service ferroviaire intermodal Anvers-Milan, fonctionnant cinq fois par semaine et retirant environ 13 000 camions des routes européennes annuellement.',
    'industry.train.connections': 'Connexions Hebdomadaires:',
    'industry.train.trucks': 'Camions Retirés:',
    'industry.train.route': 'Route:',
    'industry.train.badge': 'Transport Ferroviaire',
    
    'industry.carbon.title': 'Nouveau Marché du Carbone Européen pour les Bâtiments et le Transport Routier',
    'industry.carbon.date': '22 octobre 2025',
    'industry.carbon.content': 'Le nouveau Système d\'Échange de Quotas d\'Émission (ETS2) de l\'UE couvrira les émissions de CO2 des bâtiments et du transport routier à partir de 2027, complétant les politiques climatiques existantes pour atteindre les objectifs de neutralité 2050.',
    'industry.carbon.launch': 'Date de Lancement:',
    'industry.carbon.reduction': 'Réduction des Émissions:',
    'industry.carbon.sectors': 'Secteurs:',
    // CMA CGM Suez Article
    'article.cmacgm.title': 'CMA CGM teste les eaux avec le premier ULC Asie-Europe prêt pour le transit de la mer Rouge',
    'article.cmacgm.date': '4 novembre 2025',
    'article.cmacgm.location': 'Canal de Suez, Égypte',
    'article.cmacgm.badge': 'Maritime',
    'article.cmacgm.imageCaption': 'CMA CGM Benjamin Franklin s\'approchant du canal de Suez',
    'article.cmacgm.summary': 'Le CMA CGM Benjamin Franklin de 17 859 EVP semble prêt à entrer dans le canal de Suez pour son voyage de retour vers l\'Asie, marquant ce qui serait le plus grand porte-conteneurs à utiliser cette voie navigable vitale depuis début 2024.',
    'article.cmacgm.highlight': 'Premier navire Asie-Europe opéré par une alliance pour transiter par la mer Rouge depuis près de deux ans',
    'article.cmacgm.section1.title': 'Tentative de transit historique',
    'article.cmacgm.section1.content': 'Le navire s\'approche actuellement du port d\'Alexandrie en Égypte après avoir quitté Southampton, Royaume-Uni, le 25 octobre.',
    'article.cmacgm.section2.title': 'Signes de récupération de la mer Rouge',
    'article.cmacgm.section2.content': 'Selon les données de Lloyd\'s List Intelligence, le trafic de porte-conteneurs dans le détroit de Bab el-Mandeb a connu une certaine augmentation cette année.',
    'article.cmacgm.section3.title': 'Considérations de sécurité',
    'article.cmacgm.section3.content': 'Les navires CMA CGM auraient été escortés par la marine française pour les protéger des attaques de drones Houthis.',
    'article.cmacgm.section4.title': 'Impact sur le marché',
    'article.cmacgm.section4.content': 'Cette démarche intervient après un cessez-le-feu négocié par le président américain Donald Trump entré en vigueur le 10 octobre.',
    'article.cmacgm.impact.title': 'Importance industrielle',
    'article.cmacgm.impact.content': 'Ce transit pourrait signaler le début d\'un retour plus large de la flotte vers les routes de la mer Rouge.',

    // UPS Kuehne Article
    'article.upskuehne.title': 'UPS et Kuehne+Nagel réduisent les coûts face à des conditions de marché difficiles',
    'article.upskuehne.date': '3 novembre 2025',
    'article.upskuehne.location': 'Mondial',
    'article.upskuehne.badge': 'Réduction des coûts',
    'article.upskuehne.imageCaption': 'Opérations logistiques UPS et Kuehne+Nagel',
    'article.upskuehne.summary': 'Les grandes entreprises de logistique UPS et Kuehne+Nagel mettent en œuvre d\'importants programmes de réduction des coûts.',
    'article.upskuehne.highlight': 'Les deux entreprises se concentrent sur les réductions de coûts structurels et la rationalisation des installations',
    'article.upskuehne.section1.title': 'Initiative de réduction des coûts UPS',
    'article.upskuehne.section1.content': 'UPS connaît un marché difficile et continue le travail de réduction des coûts structurels.',
    'article.upskuehne.section2.title': 'Réponse de Kuehne+Nagel',
    'article.upskuehne.section2.content': 'Kuehne+Nagel s\'est lancé dans un programme de réduction des coûts à l\'échelle du groupe.',
    'article.upskuehne.section3.title': 'Défis à l\'échelle de l\'industrie',
    'article.upskuehne.section3.content': 'DSV navigue également dans des marchés difficiles et volatils tout en intégrant Schenker.',
    'article.upskuehne.section4.title': 'Perspectives du marché',
    'article.upskuehne.section4.content': 'Le fournisseur de logistique européen Logwin a rapporté des défis similaires.',
    'article.upskuehne.impact.title': 'Impact du secteur automobile',
    'article.upskuehne.impact.content': 'Les marchés clés de la logistique automobile ont souffert de changements structurels.',

    // Amazon Netherlands Article
    'article.amazon.title': 'Amazon investit plus de 1,4 milliard d\'euros aux Pays-Bas',
    'article.amazon.date': '4 novembre 2025',
    'article.amazon.location': 'Pays-Bas',
    'article.amazon.badge': 'Investissement',
    'article.amazon.imageCaption': 'Opérations logistiques Amazon aux Pays-Bas',
    'article.amazon.summary': 'Amazon prévoit d\'investir plus de 1,4 milliard d\'euros aux Pays-Bas au cours des trois prochaines années (2025-2027).',
    'article.amazon.highlight': 'Investissement de 1,4 milliard d\'euros sur trois ans soutenant AWS et les opérations de vente au détail',
    'article.amazon.section1.title': 'Focus d\'investissement',
    'article.amazon.section1.content': 'L\'investissement soutiendra à la fois Amazon Web Services (AWS) et l\'activité de vente au détail.',
    'article.amazon.section2.title': 'Soutien aux entrepreneurs néerlandais',
    'article.amazon.section2.content': 'Plus de 60% de tous les produits vendus sur Amazon.nl proviennent de partenaires vendeurs.',
    'article.amazon.section3.title': 'Impact économique',
    'article.amazon.section3.content': 'Amazon emploie maintenant plus de 1 000 personnes aux Pays-Bas.',
    'article.amazon.section4.title': 'Portée mondiale',
    'article.amazon.section4.content': 'Les PME néerlandaises sur Amazon atteignent des clients dans 170 pays.',
    'article.amazon.impact.title': 'Contribution au PIB',
    'article.amazon.impact.content': 'Les investissements d\'Amazon ont contribué plus de 200 millions d\'euros au PIB néerlandais en 2024.',

    // Alibaba Liege Article
    'article.alibaba.title': 'Alibaba annule l\'expansion de Liège',
    'article.alibaba.date': '5 novembre 2025',
    'article.alibaba.location': 'Liège, Belgique',
    'article.alibaba.badge': 'Expansion annulée',
    'article.alibaba.imageCaption': 'Installation logistique Alibaba Cainiao à l\'aéroport de Liège',
    'article.alibaba.summary': 'Cainiao, filiale logistique d\'Alibaba, ne procédera pas à l\'expansion prévue à l\'aéroport de Liège.',
    'article.alibaba.highlight': 'Deux entrepôts supplémentaires de 24 000 et 20 000 mètres carrés annulés',
    'article.alibaba.section1.title': 'Décision de révision stratégique',
    'article.alibaba.section1.content': 'Le géant chinois du e-commerce se concentrera sur l\'infrastructure existante.',
    'article.alibaba.section2.title': 'Opérations existantes',
    'article.alibaba.section2.content': 'Cainiao exploite un entrepôt de 30 000 m² qui a réduit les délais AliExpress de 30%.',
    'article.alibaba.section3.title': 'Impact sur la Wallonie',
    'article.alibaba.section3.content': 'La décision déçoit les ambitions de hub logistique de la Wallonie.',
    'article.alibaba.section4.title': 'Réseau européen',
    'article.alibaba.section4.content': 'Liège reste la pièce maîtresse du réseau européen de Cainiao.',
    'article.alibaba.impact.title': 'Plans futurs',
    'article.alibaba.impact.content': 'Incertain si de nouveaux entrepôts sont prévus ailleurs.',

    // InPost Iberia Article
    'article.inpost.title': 'InPost renforce ses opérations dans la péninsule ibérique',
    'article.inpost.date': '5 novembre 2025',
    'article.inpost.location': 'Espagne et Portugal',
    'article.inpost.badge': 'Expansion du réseau',
    'article.inpost.imageCaption': 'Consignes colis InPost dans la péninsule ibérique',
    'article.inpost.summary': 'InPost augmente sa capacité à 500 000 envois quotidiens, soit 220% d\'augmentation.',
    'article.inpost.highlight': 'Réseau étendu à près de 4 000 consignes, 55% disponibles 24h/24',
    'article.inpost.section1.title': 'Expansion de capacité',
    'article.inpost.section1.content': 'Réseau étendu en 2025, 99,38% des consignes accessibles 6 jours/semaine.',
    'article.inpost.section2.title': 'Améliorations opérationnelles',
    'article.inpost.section2.content': '60% d\'augmentation du personnel, 88 embauches, 13 postes permanents.',
    'article.inpost.section3.title': 'Couverture de service',
    'article.inpost.section3.content': '100% des codes postaux couverts en Espagne, Portugal et îles.',
    'article.inpost.section4.title': 'Intégration internationale',
    'article.inpost.section4.content': 'Envois transfrontaliers depuis 6 pays européens après acquisition Sending.',
    'article.inpost.impact.title': 'Prêt pour la haute saison',
    'article.inpost.impact.content': 'Préparation pour Black Friday, Noël et soldes post-fêtes.',

    // EuroCommerce Tax Article
    'article.eurocommerce.title': 'EuroCommerce : la taxe colis devrait être à l\'échelle de l\'UE',
    'article.eurocommerce.date': '5 novembre 2025',
    'article.eurocommerce.location': 'Union européenne',
    'article.eurocommerce.badge': 'Réforme politique',
    'article.eurocommerce.imageCaption': 'Douanes et traitement des colis de l\'Union européenne',
    'article.eurocommerce.summary': 'EuroCommerce pousse pour des frais unifiés de 2 euros sur les colis internationaux.',
    'article.eurocommerce.highlight': 'Frais de 2 euros sur colis hors UE, 0,50 euro pour entrepôts européens',
    'article.eurocommerce.section1.title': 'Proposition Commission européenne',
    'article.eurocommerce.section1.content': 'Frais fixes de 2 euros payés par plateformes, entrée en vigueur mars 2028.',
    'article.eurocommerce.section2.title': 'Mise en œuvre nationale',
    'article.eurocommerce.section2.content': 'Belgique prévoit frais de 2 euros dès 2026, Pays-Bas avant 2028.',
    'article.eurocommerce.section3.title': 'Risques de fragmentation',
    'article.eurocommerce.section3.content': 'Approches nationales pourraient compromettre l\'intégrité du marché unique.',
    'article.eurocommerce.section4.title': 'Impact marché',
    'article.eurocommerce.section4.content': 'Dépenses en ligne ont augmenté de 7% en 2024 à 819 milliards d\'euros.',
    'article.eurocommerce.impact.title': 'Besoin d\'harmonisation',
    'article.eurocommerce.impact.content': 'Solution UE rapide essentielle pour égalité des conditions concurrentielles.',

    
    // Risk Sensing
    'risk.title': 'Dernière Détection des Risques',
    
    'risk.cosco.title': 'Porte-conteneurs Affrété COSCO Pris dans un Différend de Frais Portuaires Chinois',
    'risk.cosco.description': 'Un porte-conteneurs exploité par COSCO a sauté les escales chinoises après avoir été soumis aux nouveaux frais portuaires visant les navires liés aux États-Unis, démontrant une application stricte même contre les entreprises d\'État.',
    'risk.cosco.date': '28 Octobre 2025',
    'risk.cosco.badge': 'Perturbation Commerciale',
    'risk.cosco.vessel': 'Navire',
    'risk.cosco.status': 'Statut',
    'risk.cosco.statusValue': 'Cherche Exemption',
    'risk.cosco.impact': 'Niveau d\'Impact',
    'risk.cosco.impactValue': 'Risque Élevé',
    
    // COSCO Boxship Article
    'article.coscoBoxship.title': 'Porte-conteneurs Affrété COSCO Pris dans un Différend de Frais Portuaires Chinois',
    'article.coscoBoxship.readTime': '4 min de lecture',
    'article.coscoBoxship.imageCaption': 'Opérations de conteneurs dans un port chinois au milieu des nouvelles réglementations de frais visant les navires liés aux États-Unis',
    'article.coscoBoxship.lead': 'Un porte-conteneurs exploité par COSCO Shipping est devenu une victime inattendue des frais portuaires de rétorsion de la Chine visant les navires liés aux États-Unis, soulignant l\'application complexe des nouvelles restrictions commerciales même contre les entreprises d\'État.',
    'article.coscoBoxship.section1.title': 'Navire Pris dans le Feu Croisé Politique',
    'article.coscoBoxship.section1.content': 'Le Halley de 2 550 EVP, affrété par COSCO Shipping dans le cadre d\'un accord de deux ans depuis avril 2025, a été contraint de sauter les escales portuaires chinoises après que les autorités aient déterminé que la participation américaine du navire dépassait le seuil de 25% fixé par le ministère chinois des Transports.',
    'article.coscoBoxship.vessel.title': 'Spécifications du Navire',
    'article.coscoBoxship.vessel.name': 'Nom du Navire',
    'article.coscoBoxship.vessel.capacity': 'Capacité',
    'article.coscoBoxship.vessel.flag': 'État du Pavillon',
    'article.coscoBoxship.vessel.charter': 'Période d\'Affrètement',
    'article.coscoBoxship.vessel.charterValue': 'Accord de 2 ans (avril 2025)',
    'article.coscoBoxship.section2.title': 'Mesures de Rétorsion',
    'article.coscoBoxship.section2.content': 'Les nouveaux frais portuaires, mis en œuvre le 14 octobre 2025, ont été introduits en rétorsion directe aux tarifs du Représentant commercial américain visant le secteur maritime chinois. Les règles démontrent l\'engagement de Pékin pour une application stricte, sans exemptions même pour les opérateurs étatiques chinois.',
    'article.coscoBoxship.section3.title': 'Défis Opérationnels',
    'article.coscoBoxship.section3.content': 'Les sources rapportent que le navire sous pavillon libérien a attendu au large de Ningbo pendant plusieurs jours pendant que COSCO cherchait une exemption des autorités. Les plans alternatifs de détournement vers Hong Kong, qui a confirmé qu\'il n\'appliquerait pas les frais du continent, ont également fait peu de progrès.',
    'article.coscoBoxship.impact.title': 'Évaluation de l\'Impact Industriel',
    'article.coscoBoxship.impact.point1': 'Démontre l\'application stricte des restrictions commerciales sans favoritisme',
    'article.coscoBoxship.impact.point2': 'Crée une incertitude opérationnelle pour les compagnies maritimes internationales',
    'article.coscoBoxship.impact.point3': 'Peut affecter d\'autres navires affrétés COSCO avec des structures de propriété similaires',
    'article.coscoBoxship.impact.point4': 'Souligne la complexité des tensions commerciales US-Chine dans le secteur maritime',
    'article.coscoBoxship.section4.title': 'Implications Plus Larges',
    'article.coscoBoxship.section4.content': 'Les experts de l\'industrie suggèrent que ce cas pourrait signaler l\'engagement de la Chine envers une application impartiale des restrictions commerciales, affectant potentiellement plus de navires dans la flotte affrétée de COSCO. L\'incident souligne la complexité croissante de naviguer les tensions commerciales US-Chine dans le secteur maritime.',
    'risk.strikes.title': 'Ports de Rotterdam et Anvers Toujours Congestionnés Après la Fin des Grèves',
    'risk.strikes.location': 'Pays-Bas & Belgique',
    'risk.strikes.content': 'Bien que les grèves soient officiellement terminées dans les ports de Rotterdam et d\'Anvers, une congestion importante persiste avec des arriérés de navires continuant d\'impacter les opérations logistiques européennes.',
    'risk.strikes.impact': 'Évaluation de l\'Impact',
    'risk.strikes.ships': 'Navires en Attente (Pic):',
    'risk.strikes.duration': 'Durée de la Grève:',
    'risk.strikes.recovery': 'Statut de Récupération:',
    'risk.strikes.badge': 'Perturbation Portuaire',
    
    'risk.dashboard.title': 'Tableau de Bord de Surveillance des Risques',
    'risk.dashboard.subtitle': 'Indicateurs de risque logistique en temps réel',
    'risk.dashboard.congestion': 'Congestion Portuaire',
    'risk.dashboard.volatility': 'Volatilité des Taux de Fret',
    'risk.dashboard.rail': 'Statut du Réseau Ferroviaire',
    'risk.dashboard.regulatory': 'Changements Réglementaires',
    
    // Logistics Data
    'data.title': 'Indice Macroéconomique',
    'data.diesel.title': 'Prix du Carburant Diesel des Pays Européens',
    'data.diesel.subtitle': 'Prix mensuels du diesel par litre par pays (EUR) - Janvier 2024 à Octobre 2025',
    'data.diesel.insights': 'Informations Clés',
    'data.diesel.average': '• Tendance du prix diesel UE moyen de €1,52 à €1,63/litre',
    'data.diesel.lowest': '• Moyenne la plus basse était €1,51/litre en février 2024',
    'data.diesel.highest': '• Moyennes les plus élevées ont atteint €1,63/litre en 2025',
    'data.diesel.change': '• Augmentation globale de €0,11/litre sur 22 mois',
    
    'data.inflation.title': 'Taux d\'Inflation des Pays Européens',
    'data.inflation.subtitle': 'Taux d\'inflation annuels par pays (%) - Janvier 2024 à Octobre 2025',
    'data.inflation.update': 'Mise à jour Octobre 2025',
    'data.inflation.current': 'Taux Actuel:',
    'data.inflation.target': 'Objectif BCE:',
    'data.inflation.monthly': 'Changement Mensuel:',
    'data.inflation.core': 'Inflation Sous-jacente:',
    
    'data.minimumWage.title': 'Salaires Minimums des Pays Européens 2025',
    'data.minimumWage.subtitle': 'Taux de salaire minimum réels par pays (EUR/heure) - 2025',
    'data.minimumWage.insights': 'Informations Clés',
    'data.minimumWage.highest': '• Pays-Bas en tête avec €12,83/heure',
    'data.minimumWage.lowest': '• Pologne a le taux le plus bas à €4,30/heure',
    'data.minimumWage.average': '• Europe de l\'Ouest significativement plus élevée que l\'Europe de l\'Est',
    'data.minimumWage.stability': '• Différence salariale de 3x entre les taux les plus élevés et les plus bas',
    
    // Common
    'common.share': 'Partager',
    'common.back': 'Retour',
    'common.readMore': 'Lire plus',
    'common.declining': 'En Déclin',
    'common.expansion': 'Expansion',
    'common.newPolicy': 'Nouvelle Politique',
    'common.highRisk': 'Risque Élevé',
    'common.high': 'Élevé',
    'common.medium': 'Moyen',
    'common.normal': 'Normal',
    'common.monitoring': 'Surveillance',
    'common.ongoing': 'En Cours',
    
    // Articles
    'article.relatedArticles': 'Articles Connexes',
    
    // Freight Rates Article
    'article.freight.title': 'Les Entreprises Logistiques Européennes Subissent l\'Impact de la Chute des Taux de Fret',
    'article.freight.date': '21 octobre 2024',
    'article.freight.readTime': '5 min de lecture',
    'article.freight.imageCaption': 'Grues à conteneurs au port de Bremerhaven manipulant des navires Maersk dans un contexte de baisse des taux de fret',
    'article.freight.lead': 'Les entreprises de transport maritime et de logistique européennes subissent une pression importante alors que les taux de conteneurs chutent à leur plus bas niveau depuis janvier 2024, causé par la suroffre industrielle et la demande réduite suite aux nouvelles politiques commerciales américaines.',
    'article.freight.section1.title': 'Montée des Pressions du Marché',
    'article.freight.section1.content': 'La combinaison de la surcapacité du marché du transport maritime de conteneurs et de l\'affaiblissement des volumes commerciaux mondiaux a créé une tempête parfaite pour les fournisseurs de logistique européens. Les grands transporteurs comme Maersk sont mieux positionnés grâce aux contrats à long terme, mais les petits opérateurs font face à une pression immédiate sur les marges.',
    'article.freight.chart.title': 'Tendances des Taux de Fret et Volumes',
    'article.freight.chart.description': 'Taux de fret mensuels (USD) et volumes de conteneurs (Millions EVP)',
    'article.freight.section2.title': 'Analyse d\'Impact Régional',
    'article.freight.section2.content': 'Les ports d\'Europe du Nord connaissent l\'impact le plus significatif, Rotterdam et Hambourg enregistrant un débit réduit. Les effets d\'entraînement s\'étendent aux réseaux logistiques intérieurs, affectant les opérations ferroviaires et de camionnage à travers le continent.',
    'article.freight.keyStats.title': 'Indicateurs Clés du Marché',
    'article.freight.keyStats.rateDecline': 'Baisse des taux depuis juillet',
    'article.freight.keyStats.currentRate': 'Taux moyen actuel (USD)',
    'article.freight.keyStats.volumeTEU': 'EVP traités (Oct)',
    'article.freight.keyStats.waitingShips': 'Navires en attente de poste',
    'article.freight.section3.title': 'Réponse de l\'Industrie',
    'article.freight.section3.content': 'Les entreprises de logistique mettent en œuvre des mesures de réduction des coûts et explorent des sources de revenus alternatives. Certaines accélèrent les efforts de numérisation pour améliorer l\'efficacité opérationnelle et réduire les frais généraux.',
    'article.freight.section4.title': 'Perspectives et Implications',
    'article.freight.section4.content': 'Les analystes s\'attendent à ce que le ralentissement actuel persiste jusqu\'au T1 2025, la reprise dépendant de la normalisation du commerce mondial et des ajustements de capacité par les grands transporteurs. Les entreprises avec des portefeuilles de services diversifiés sont susceptibles de mieux résister à la tempête.',
    
    // Train Connection Article
    'article.train.title': 'FS Logistix Reprend la Connexion Ferroviaire entre Anvers et Milan',
    'article.train.date': '21 octobre 2024',
    'article.train.readTime': '4 min de lecture',
    'article.train.imageCaption': 'Terminal intermodal FS Logistix au Hub Principal d\'Anvers facilitant la nouvelle connexion Milan',
    'article.train.lead': 'La société logistique italienne publique FS Logistix a pris avec succès le contrôle du corridor ferroviaire stratégique Anvers-Milan, marquant une expansion significative des capacités de transport intermodal européennes.',
    'article.train.section1.title': 'Détails de l\'Acquisition Stratégique',
    'article.train.section1.content': 'La reprise de l\'opérateur belge Lineas représente un investissement de 45 millions d\'euros dans l\'infrastructure ferroviaire européenne. FS Logistix opère maintenant à travers un partenariat avec Mercitalia Intermodal, utilisant les locomotives Lineas sous un accord de traction complet.',
    'article.train.route.title': 'Spécifications de l\'Itinéraire',
    'article.train.route.description': 'Détails complets du voyage d\'Anvers à Milan',
    'article.train.route.departure': 'Terminal Hub Principal, Belgique',
    'article.train.route.arrival': 'Terminal Segrate, Italie',
    'article.train.route.terminalSize': 'Surface du terminal',
    'article.train.route.transitTime': 'Temps de transit',
    'article.train.section2.title': 'Impact Environnemental',
    'article.train.section2.content': 'Le service représente un pas majeur vers une logistique durable, retirant des milliers de camions des autoroutes européennes annuellement. Chaque train peut transporter l\'équivalent de 52 chargements de camions, réduisant significativement les émissions de carbone et la congestion routière.',
    'article.train.environmental.title': 'Métriques de Durabilité',
    'article.train.environmental.trucksRemoved': 'Camions retirés annuellement',
    'article.train.environmental.co2Reduction': 'Réduction des émissions CO2',
    'article.train.environmental.weeklyFrequency': 'Départs hebdomadaires',
    'article.train.section3.title': 'Excellence Opérationnelle',
    'article.train.section3.content': 'Le terminal Hub Principal d\'Anvers s\'étend sur 200 000 mètres carrés avec huit voies de 700 mètres, trois grues portiques et six cavaliers. Cette infrastructure soutient une manutention efficace des conteneurs et des temps de rotation rapides.',
    'article.train.section4.title': 'Plans d\'Expansion Future',
    'article.train.section4.content': 'FS Logistix prévoit d\'étendre les connexions vers des destinations italiennes supplémentaires incluant Rome et Naples, tout en explorant les opportunités de services vers la France, l\'Allemagne et l\'Europe de l\'Est via le hub d\'Anvers.',
    'article.train.partnership.title': 'Structure de Partenariat',
    'article.train.partnership.fsRole': 'Opérateur de service et direction commerciale',
    'article.train.partnership.mercitaliaRole': 'Gestion des opérations ferroviaires',
    'article.train.partnership.lineasRole': 'Services de locomotive et traction',
    
    // Carbon Market Article
    'article.carbon.title': 'Nouveau Marché du Carbone Européen pour les Bâtiments et le Transport Routier',
    'article.carbon.date': '21 octobre 2024',
    'article.carbon.readTime': '6 min de lecture',
    'article.carbon.imageCaption': 'Bâtiment de la Commission européenne représentant la nouvelle législation du marché carbone ETS2',
    'article.carbon.lead': 'Le système ETS2 révolutionnaire de l\'Union européenne révolutionnera la tarification du carbone pour les secteurs du bâtiment et des transports, créant le marché du carbone le plus complet au monde d\'ici 2027.',
    'article.carbon.section1.title': 'Aperçu du Cadre ETS2',
    'article.carbon.section1.content': 'Le nouveau Système d\'Échange de Quotas d\'Émission couvre la distribution de carburant pour les bâtiments, le transport routier et les secteurs industriels supplémentaires non inclus dans l\'ETS UE original. Cette expansion représente une opportunité de marché de 104 milliards d\'euros.',
    'article.carbon.chart.title': 'Objectifs d\'Émissions par Secteur',
    'article.carbon.chart.description': 'Émissions actuelles vs objectifs 2030 par secteur (Millions de tonnes CO2)',
    'article.carbon.chart.buildings': 'Bâtiments',
    'article.carbon.chart.transport': 'Transport',
    'article.carbon.chart.industry': 'Petite Industrie',
    'article.carbon.chart.current': 'Émissions Actuelles',
    'article.carbon.chart.target2030': 'Objectif 2030',
    'article.carbon.section2.title': 'Calendrier de Mise en Œuvre',
    'article.carbon.section2.content': 'Le système se lance en 2027 avec un prix plancher de 45 euros par tonne CO2. Les États membres recevront des revenus pour soutenir les investissements de transition verte et protéger les ménages vulnérables des augmentations de prix de l\'énergie.',
    'article.carbon.keyFeatures.title': 'Caractéristiques Clés',
    'article.carbon.keyFeatures.startDate': 'Année de lancement du système',
    'article.carbon.keyFeatures.reductionTarget': 'Réduction des émissions d\'ici 2030',
    'article.carbon.keyFeatures.priceFloor': 'Prix minimum du carbone',
    'article.carbon.keyFeatures.marketValue': 'Valeur marchande estimée',
    'article.carbon.section3.title': 'Mécanismes de Marché',
    'article.carbon.section3.content': 'L\'ETS2 inclut des mécanismes automatiques de stabilisation des prix et le recyclage des revenus vers les États membres. Une Réserve de Stabilité du Marché gérera l\'offre de quotas pour prévenir une volatilité excessive des prix.',
    'article.carbon.timeline.title': 'Calendrier de Mise en Œuvre',
    'article.carbon.timeline.preparation': 'Phase de conception et préparation du système',
    'article.carbon.timeline.launch': 'L\'ETS2 devient opérationnel',
    'article.carbon.timeline.target': 'Objectif de réduction des émissions de 42%',
    'article.carbon.section4.title': 'Implications Économiques',
    'article.carbon.section4.content': 'Le marché du carbone générera des revenus significatifs pour les investissements verts tout en incitant à l\'adoption de technologies propres. Les industries se préparent déjà à la transition avec des améliorations d\'efficacité et des stratégies de carburants alternatifs.',
    
    // AWS Outage Article
    'article.aws.title': 'La Panne AWS Perturbe les Chaînes d\'Approvisionnement Mondiales',
    'article.aws.date': '25 octobre 2024',
    'article.aws.readTime': '5 min de lecture',
    'article.aws.imageCaption': 'Perturbation de l\'infrastructure cloud mondiale affectant les opérations de chaîne d\'approvisionnement',
    'article.aws.lead': 'Une panne majeure d\'Amazon Web Services durant plus de 4 heures a causé des perturbations généralisées aux systèmes de gestion de chaîne d\'approvisionnement en Europe et Amérique du Nord, soulignant la dépendance critique à l\'infrastructure cloud.',
    'article.aws.section1.title': 'Évaluation de l\'Impact de la Panne',
    'article.aws.section1.content': 'La perturbation a affecté la région US-East-1 d\'AWS, se répercutant sur les services européens et impactant les plateformes logistiques, systèmes de gestion d\'inventaire et applications de suivi en temps réel utilisées par les grands détaillants et fournisseurs logistiques.',
    'article.aws.chart.title': 'Récupération des Services Régionaux',
    'article.aws.chart.description': 'Disponibilité des services pendant et après la panne par région',
    'article.aws.chart.usEast': 'Est des États-Unis',
    'article.aws.chart.europe': 'Europe',
    'article.aws.chart.asiaPacific': 'Asie-Pacifique',
    'article.aws.chart.affected': 'Impact de Pointe',
    'article.aws.chart.recovered': 'Taux de Récupération',
    'article.aws.section2.title': 'Perturbations de la Chaîne d\'Approvisionnement',
    'article.aws.section2.content': 'Les plateformes e-commerce ont connu des retards de traitement des commandes, tandis que les entreprises logistiques ont perdu la visibilité en temps réel sur le suivi des expéditions. Les systèmes de gestion d\'entrepôt sont tombés en panne, causant des écarts d\'inventaire et des retards d\'exécution.',
    'article.aws.sectorChart.title': 'Distribution de l\'Impact par Secteur',
    'article.aws.sectorChart.description': 'Pourcentage de services affectés par secteur industriel',
    'article.aws.sectors.ecommerce': 'E-commerce',
    'article.aws.sectors.logistics': 'Logistique',
    'article.aws.sectors.manufacturing': 'Fabrication',
    'article.aws.sectors.retail': 'Vente au détail',
    'article.aws.keyStats.title': 'Statistiques de la Panne',
    'article.aws.keyStats.duration': 'Durée totale de la panne',
    'article.aws.keyStats.estimatedLoss': 'Impact économique estimé',
    'article.aws.keyStats.affectedServices': 'Services affectés',
    'article.aws.keyStats.recoveryRate': 'Récupération complète atteinte',
    'article.aws.section3.title': 'Opérations de Récupération',
    'article.aws.section3.content': 'AWS a mis en place des protocoles d\'urgence pour restaurer les services critiques, priorisant les applications de chaîne d\'approvisionnement et logistiques. L\'entreprise a fourni des mises à jour régulières et accordé des crédits de service aux clients affectés.',
    'article.aws.timeline.title': 'Chronologie de l\'Incident',
    'article.aws.timeline.start': 'Dégradation initiale du service détectée',
    'article.aws.timeline.peak': 'Impact de pointe dans toutes les régions',
    'article.aws.timeline.resolution': 'Restauration complète du service terminée',
    'article.aws.section4.title': 'Réponse de l\'Industrie',
    'article.aws.section4.content': 'L\'incident a incité les gestionnaires de chaîne d\'approvisionnement à réévaluer leur dépendance au cloud et leurs stratégies de récupération après sinistre. Beaucoup d\'entreprises considèrent maintenant des architectures multi-cloud pour réduire les points de défaillance uniques.',
    'article.aws.recommendations.title': 'Stratégies d\'Atténuation des Risques',
    'article.aws.recommendations.multiCloud': 'Implémenter une architecture multi-cloud',
    'article.aws.recommendations.backupSystems': 'Maintenir des systèmes de sauvegarde hors ligne',
    'article.aws.recommendations.monitoring': 'Surveillance et alertes améliorées',
    'article.aws.recommendations.contingency': 'Développer des plans d\'urgence complets',
    
    // Port Strikes Article
    'article.strikes.title': 'Les Grèves Portuaires s\'Apaisent à Rotterdam et Anvers, mais la Congestion Persiste',
    'article.strikes.date': '22 octobre 2024',
    'article.strikes.readTime': '4 min de lecture',
    'article.strikes.imageCaption': 'Navires porte-conteneurs en file d\'attente devant le port de Rotterdam pendant les grèves récentes',
    'article.strikes.lead': 'Bien que l\'action industrielle soit officiellement terminée dans les deux ports les plus fréquentés d\'Europe, les conséquences continuent d\'impacter les opérations logistiques avec des arriérés de navires et un traitement de fret retardé.',
    'article.strikes.section1.title': 'Résolution des Grèves',
    'article.strikes.section1.content': 'Rotterdam et Anvers ont tous deux conclu des accords avec les travailleurs grévistes, mais le processus de récupération devrait prendre plusieurs semaines. Les autorités portuaires travaillent 24h/24 pour éliminer l\'arriéré de navires en attente.',
    'article.strikes.chart.title': 'Chronologie de la Congestion des Navires',
    'article.strikes.chart.description': 'Nombre de navires en attente d\'attribution de poste',
    'article.strikes.chart.oct5': '5 Oct',
    'article.strikes.chart.oct8': '8 Oct',
    'article.strikes.chart.oct10': '10 Oct',
    'article.strikes.chart.oct15': '15 Oct',
    'article.strikes.chart.oct18': '18 Oct',
    'article.strikes.chart.oct22': '22 Oct',
    'article.strikes.section2.title': 'Problèmes Spécifiques aux Ports',
    'article.strikes.section2.content': 'Rotterdam a fait face à des grèves d\'équipes de saisissage concernant des disputes salariales, tandis qu\'Anvers a traité avec des grèves de pilotes liées aux réformes des retraites. La nature différente de ces disputes a nécessité des processus de négociation séparés.',
    'article.strikes.rotterdam.issue': 'Problème de Grève',
    'article.strikes.rotterdam.issueValue': 'Disputes Salariales',
    'article.strikes.rotterdam.duration': 'Durée de la Grève',
    'article.strikes.rotterdam.peakShips': 'Congestion de Pointe',
    'article.strikes.antwerp.issue': 'Problème de Grève',
    'article.strikes.antwerp.issueValue': 'Réformes des Retraites',
    'article.strikes.antwerp.duration': 'Durée de la Grève',
    'article.strikes.antwerp.peakShips': 'Congestion de Pointe',
    'article.strikes.section3.title': 'Impact Économique',
    'article.strikes.section3.content': 'Les grèves combinées ont résulté en pertes économiques significatives, avec des expéditions retardées affectant les chaînes d\'approvisionnement à travers l\'Europe. Les détaillants et fabricants ont fait face à des pénuries d\'inventaire et des coûts logistiques accrus.',
    'article.strikes.economicImpact.title': 'Évaluation de l\'Impact Financier',
    'article.strikes.economicImpact.dailyLoss': 'Perte économique quotidienne',
    'article.strikes.economicImpact.affectedTEU': 'EVP affectés',
    'article.strikes.economicImpact.delayIncrease': 'Augmentation moyenne des retards',
    'article.strikes.economicImpact.avgDelay': 'Retard de traitement moyen',
    'article.strikes.section4.title': 'Progrès de la Récupération',
    'article.strikes.section4.content': 'Les opérations portuaires reviennent progressivement à la normale, avec priorité donnée au fret sensible au temps et aux marchandises périssables. Du personnel supplémentaire et des heures d\'exploitation étendues sont mises en place pour accélérer la récupération.',
    'article.strikes.recovery.title': 'Statut Actuel de Récupération',
    'article.strikes.recovery.rotterdam': 'Opérations de Rotterdam',
    'article.strikes.recovery.rotterdamStatus': 'Pleinement Opérationnel',
    'article.strikes.recovery.antwerp': 'Opérations d\'Anvers',
    'article.strikes.recovery.antwerpStatus': 'En Récupération',
    'article.strikes.recovery.normalOps': 'Retour à la Normale',
    
    // Source Section
    'article.source.title': 'Source Originale',
    'article.source.description': 'Lisez l\'article original de la publication source:',
    'article.source.readOriginal': 'Lire l\'Article Original',
    'article.source.publisher': 'Éditeur',
    
    // Logistics Partners Insights
    // Market Insights
    'market.title': 'Aperçus du Marché',
    'market.subtitle': 'Rapport Mensuel d\'Aperçus Logistiques - Novembre 2025',
    'market.executiveSummary.title': 'Résumé Exécutif',
    'market.roadFreight.title': 'Marché Européen du Fret Routier',
    'market.roadFreight.summary': 'Les tarifs se sont stabilisés à des niveaux élevés après les pics du T2, avec convergence des tarifs spot et contractuels. Cependant, la capacité reste critique, exacerbée par une grave pénurie de chauffeurs de plus de 426 000 postes en Europe.',
    'market.roadFreight.keyPoint1': 'Pénurie de chauffeurs : 426 000+ postes non pourvus',
    'market.roadFreight.keyPoint2': 'Tarifs stabilisés à l\'indice 132,2-134,06',
    'market.roadFreight.keyPoint3': 'Baisse de 15,4% des immatriculations de camions UE',
    'market.oceanFreight.title': 'Marché Mondial du Fret Maritime',
    'market.oceanFreight.summary': 'Les tarifs de fret maritime diminuent par rapport à leurs sommets du S1 2025, car les volumes anticipés pour atténuer l\'incertitude tarifaire ont diminué, entraînant un ramollissement de la demande.',
    'market.oceanFreight.keyPoint1': 'SCFI composite à 1 550,70 points (31 oct)',
    'market.oceanFreight.keyPoint2': 'Fiabilité mondiale à 65,2% en septembre',
    'market.oceanFreight.keyPoint3': 'Retard moyen pour navires en retard : 4,88 jours',
    'market.airFreight.title': 'Marché Mondial du Fret Aérien',
    'market.airFreight.summary': 'Le marché du fret aérien montre une demande stable dans un contexte d\'amélioration de la capacité, particulièrement sur les routes européennes bénéficiant des horaires passagers d\'été.',
    'market.airFreight.keyPoint1': 'CTKs en hausse de 2,9% en glissement annuel en septembre',
    'market.airFreight.keyPoint2': 'Capacité (ACTKs) a crû de 3,0% en parallèle',
    'market.airFreight.keyPoint3': 'Mandats ICS2 et SAF augmentent les coûts',
    'market.realEstate.title': 'Immobilier et Entreposage Européens',
    'market.realEstate.summary': 'Le marché immobilier logistique européen connaît un ralentissement aligné sur la reprise économique fragile. Bien que l\'intérêt des investisseurs demeure pour les emplacements de premier plan avec des locataires de haute qualité, le développement spéculatif a diminué.',
    'market.realEstate.keyPoint1': 'Absorption d\'entrepôts au plus bas depuis 2014',
    'market.realEstate.keyPoint2': 'Rendements prime stables à 4,50% en Allemagne',
    'market.realEstate.keyPoint3': 'L\'Espagne montre sa résilience comme surperformante',
    'market.recommendations.title': 'Actions Immédiates pour les Expéditeurs',
    'market.recommendations.point1': 'Diversifier le mix de transporteurs pour capacité stable et spot',
    'market.recommendations.point2': 'Implémenter pre-pulls et drop trailers',
    'market.recommendations.point3': 'Améliorer flexibilité des créneaux avec heures creuses',
    'market.recommendations.point4': 'Ajouter 7-12 jours de tampon pour expéditions Asie-Europe',

    'partners.title': 'Perspectives des Partenaires Logistiques',
    'partners.kuehne.title': 'L\'Europe doit investir dans l\'infrastructure portuaire pour la résilience stratégique',
    'partners.kuehne.subtitle': 'Le PDG de Maersk et la direction d\'APMT appellent à une stratégie portuaire européenne unifiée',
    'partners.kuehne.badge': 'Novembre 2025',
    'partners.kuehne.keyTakeaways': 'Messages Clés',
    'partners.kuehne.additionalInsights': 'Implications Stratégiques',
    'partners.kuehne.takeaway1': 'L\'Europe a besoin d\'investissements sérieux dans l\'infrastructure portuaire pour soulager la congestion',
    'partners.kuehne.takeaway2': 'La capacité portuaire est rare à travers le continent, nécessitant une approche unifiée',
    'partners.kuehne.takeaway3': 'L\'investissement doit suivre le rythme de la croissance commerciale et des réalités géopolitiques',
    'partners.kuehne.takeaway4': 'L\'influence chinoise dans les grands ports européens soulève des préoccupations stratégiques',
    'partners.kuehne.takeaway5': 'Les grands ports du nord de l\'Europe font face à une congestion persistante en 2025',
    'partners.kuehne.takeaway6': 'Un tsunami de tonnage attendu dans les années à venir mettra à rude épreuve la capacité portuaire',
    'partners.kuehne.takeaway7': 'L\'infrastructure héritée n\'est pas conçue pour la prochaine génération de navires plus grands',
    'partners.kuehne.takeaway8': 'Les ports devraient être considérés comme des services publics critiques comme les aéroports',
    'partners.kuehne.source': 'Kuehne+Nagel Maritime Intelligence',
    'partners.kuehne.sourceDescription': 'Analyse stratégique de l\'infrastructure portuaire européenne et des défis de capacité',
    'partners.kuehne.viewTracker': 'Lire l\'Analyse Complète',
    
    // Footer
    'footer.title': 'LogiSense Portal',
    'footer.subtitle': 'Actualités Logistiques Européennes & Intelligence des Risques',
    'footer.updated': 'Dernière mise à jour: 7 novembre 2025 | Sources de données: IRU, Ti Insight, Shanghai Shipping Exchange, Drewry Maritime, Sea-Intelligence, IATA, Commission Européenne, CBRE, Cushman & Wakefield',
    
    // Chart labels
    'chart.rate': 'Taux (USD)',
    'chart.inflationRate': 'Taux d\'Inflation',
    'chart.pricePerLiter': 'Prix par Litre',
    
    // Countries
    'country.germany': 'Allemagne',
    'country.france': 'France',
    'country.netherlands': 'Pays-Bas',
    'country.belgium': 'Belgique',
    'country.italy': 'Italie',
    
    // Months
    'month.jan': 'Jan',
    'month.feb': 'Fév',
    'month.mar': 'Mar',
    'month.apr': 'Avr',
    'month.may': 'Mai',
    'month.jun': 'Jun',
    'month.jul': 'Juil',
    'month.aug': 'Août',
    'month.sep': 'Sep',
    'month.oct': 'Oct',
    'month.nov': 'Nov',
    'month.dec': 'Déc',
  },
  ko: {
    // Header
    'header.title': 'LogiSense Portal',
    'header.subtitle': '유럽 물류 뉴스 및 위험 인텔리전스',
    'header.week': '45주차, 2025',
    
    // Navigation
    'nav.industry': '산업 하이라이트',
    'nav.risk': '위험 감지',
    'nav.data': '물류 데이터',
    
    // Industry Highlights
    'industry.title': '45주차 산업 하이라이트',
    'industry.freight.title': '유럽 물류 기업들, 화물 운임 하락의 영향에 직면',
    'industry.freight.date': '2025년 10월 15일',
    'industry.freight.content': '유럽 해운 및 물류 기업들이 압박을 받고 있습니다. 해상 컨테이너 운임이 2024년 1월 이후 최저치로 급락했으며, 이는 업계 공급 과잉과 새로운 미국 관세로 인한 수요 약화가 원인입니다.',
    'industry.freight.chart': '화물 운임 추세',
    'industry.freight.badge': '화물 운임',
    
    'industry.train.title': 'FS Logistix, 안트베르펜-밀라노 간 철도 연결 인수',
    'industry.train.date': '2025년 10월 18일',
    'industry.train.content': '이탈리아의 FS Logistix가 안트베르펜-밀라노 간 복합운송 철도 서비스를 인수했으며, 주 5회 운행하여 연간 약 13,000대의 트럭을 유럽 도로에서 제거하고 있습니다.',
    'industry.train.connections': '주간 연결:',
    'industry.train.trucks': '제거된 트럭:',
    'industry.train.route': '노선:',
    'industry.train.badge': '철도 운송',
    
    'industry.carbon.title': '건물 및 도로 운송을 위한 유럽의 새로운 탄소 시장',
    'industry.carbon.date': '2025년 10월 22일',
    'industry.carbon.content': 'EU의 새로운 배출권 거래제(ETS2)는 2027년부터 건물과 도로 운송의 CO2 배출을 다루며, 2050년 탄소 중립 목표 달성을 위한 기존 기후 정책을 보완할 것입니다.',
    'industry.carbon.launch': '출시일:',
    'industry.carbon.reduction': '배출 감축:',
    'industry.carbon.sectors': '부문:',
    // CMA CGM Suez Article
    'article.cmacgm.title': 'CMA CGM, 첫 아시아-유럽 ULC로 홍해 통행 시험',
    'article.cmacgm.date': '2025년 11월 4일',
    'article.cmacgm.location': '수에즈 운하, 이집트',
    'article.cmacgm.badge': '해운',
    'article.cmacgm.imageCaption': '수에즈 운하에 접근하는 CMA CGM Benjamin Franklin',
    'article.cmacgm.summary': '17,859 TEU CMA CGM Benjamin Franklin이 아시아로의 귀항 여행을 위해 수에즈 운하에 진입할 준비를 하고 있어, 2024년 초 이후 이 중요한 수로를 이용하는 가장 큰 컸테이너선이 될 것입니다.',
    'article.cmacgm.highlight': '거의 2년 만에 홍해를 통행하는 첫 연합 운영 아시아-유럽 선박',
    'article.cmacgm.section1.title': '역사적 통행 시도',
    'article.cmacgm.section1.content': '이 선박은 10월 25일 영국 사우샘프턴을 출발한 후 현재 이집트의 알렉산드리아 항구에 접근하고 있습니다.',
    'article.cmacgm.section2.title': '홍해 회복 신호',
    'article.cmacgm.section2.content': 'Lloyd\'s List Intelligence 데이터에 따르면, 바브 엘만데브 해협을 통과하는 컸테이너선 교통이 올해 어느 정도 증가했습니다.',
    'article.cmacgm.section3.title': '보안 고려사항',
    'article.cmacgm.section3.content': 'CMA CGM 선박들은 후시 드론 공격으로부터 보호하기 위해 프랑스 해군의 호위를 받았다고 보고되었습니다.',
    'article.cmacgm.section4.title': '시장 영향',
    'article.cmacgm.section4.content': '이 조치는 10월 10일 발효된 도널드 트럼프 대통령이 중재한 휴전 후에 나왔습니다.',
    'article.cmacgm.impact.title': '산업 중요성',
    'article.cmacgm.impact.content': '이 통행은 홍해 항로로의 더 광범위한 함대 복귀의 시작을 신호할 수 있습니다.',

    // UPS Kuehne Article
    'article.upskuehne.title': 'UPS와 Kuehne+Nagel, 어려운 시장 상황에서 비용 절감',
    'article.upskuehne.date': '2025년 11월 3일',
    'article.upskuehne.location': '전 세계',
    'article.upskuehne.badge': '비용 절감',
    'article.upskuehne.imageCaption': 'UPS와 Kuehne+Nagel 물류 운영',
    'article.upskuehne.summary': '주요 물류 회사 UPS와 Kuehne+Nagel이 어려운 시장 상황을 헤쳐나가며 상당한 비용 절감 프로그램을 시행하고 있습니다.',
    'article.upskuehne.highlight': '두 회사 모두 구조적 비용 절감과 시설 합리화에 집중',
    'article.upskuehne.section1.title': 'UPS 비용 절감 이니셔티브',
    'article.upskuehne.section1.content': 'UPS는 도전적인 시장을 경험하고 있으며 구조적 비용 절감 작업을 계속하고 있습니다.',
    'article.upskuehne.section2.title': 'Kuehne+Nagel 대응',
    'article.upskuehne.section2.content': 'Kuehne+Nagel은 3분기 대부분의 사업에서 지속적인 수익 감소에 직면하여 그룹 전체 비용 절감 프로그램에 착수했습니다.',
    'article.upskuehne.section3.title': '업계 전반의 도전',
    'article.upskuehne.section3.content': 'DSV 또한 Schenker를 통합하면서 어렵고 변동성이 큰 시장을 헤쳐나가고 있습니다.',
    'article.upskuehne.section4.title': '시장 전망',
    'article.upskuehne.section4.content': '유럽 물류 제공업체 Logwin도 비슷한 도전을 보고했습니다.',
    'article.upskuehne.impact.title': '자동차 부문 영향',
    'article.upskuehne.impact.content': '주요 자동차 물류 시장이 구조적 변화로 인해 어려움을 겪었습니다.',

    // Amazon Netherlands Article
    'article.amazon.title': '아마존, 네덜란드에 14억 유로 이상 투자',
    'article.amazon.date': '2025년 11월 4일',
    'article.amazon.location': '네덜란드',
    'article.amazon.badge': '투자',
    'article.amazon.imageCaption': '네덜란드 아마존 물류 운영',
    'article.amazon.summary': '아마존이 향후 3년간(2025-2027) 네덜란드에 14억 유로 이상을 투자할 계획입니다.',
    'article.amazon.highlight': 'AWS와 소매 운영을 지원하는 3년간 14억 유로 투자',
    'article.amazon.section1.title': '투자 초점',
    'article.amazon.section1.content': '이 투자는 Amazon Web Services(AWS)와 소매 사업 모두를 지원할 것입니다.',
    'article.amazon.section2.title': '네덜란드 기업가 지원',
    'article.amazon.section2.content': 'Amazon.nl에서 판매되는 모든 제품의 60% 이상이 판매 파트너로부터 나옵니다.',
    'article.amazon.section3.title': '경제적 영향',
    'article.amazon.section3.content': '아마존은 현재 네덜란드 전역에서 1,000명 이상을 고용하고 있습니다.',
    'article.amazon.section4.title': '글로벌 도달 범위',
    'article.amazon.section4.content': '아마존의 네덜란드 중소기업들이 170개국의 고객에게 도달하고 있습니다.',
    'article.amazon.impact.title': 'GDP 기여도',
    'article.amazon.impact.content': '아마존의 투자는 2024년 네덜란드 GDP에 2억 유로 이상 기여했습니다.',

    // Alibaba Liege Article
    'article.alibaba.title': '알리바바, 리에주 확장 계획 취소',
    'article.alibaba.date': '2025년 11월 5일',
    'article.alibaba.location': '리에주, 벨기에',
    'article.alibaba.badge': '확장 취소',
    'article.alibaba.imageCaption': '리에주 공항 알리바바 카이니오 물류 시설',
    'article.alibaba.summary': '알리바바의 물류 자회사 카이니오가 리에주 공항 확장 계획을 진행하지 않기로 결정했습니다.',
    'article.alibaba.highlight': '24,000년 20,000 평방미터 규모의 추가 창고 2개 취소',
    'article.alibaba.section1.title': '전략적 검토 결정',
    'article.alibaba.section1.content': '중국 이커머스 거대 기업이 기존 인프라 운영에 집중하기로 결정했습니다.',
    'article.alibaba.section2.title': '기존 운영',
    'article.alibaba.section2.content': '카이니오는 30,000㎡ 창고를 운영하며 알리익스프레스 배송 시간을 30% 단축했습니다.',
    'article.alibaba.section3.title': '와론 지역 영향',
    'article.alibaba.section3.content': '이 결정은 아시아 이커머스 기업의 물류 허브가 되려는 와론의 야망을 좌절시켰습니다.',
    'article.alibaba.section4.title': '유럽 네트워크',
    'article.alibaba.section4.content': '리에주는 카이니오 유럽 네트워크의 핵심으로 남아있습니다.',
    'article.alibaba.impact.title': '향후 계획',
    'article.alibaba.impact.content': '대륙 다른 곳에 새 창고 개설 계획은 불분명합니다.',

    // InPost Iberia Article
    'article.inpost.title': 'InPost, 이베리아 반도 운영 강화',
    'article.inpost.date': '2025년 11월 5일',
    'article.inpost.location': '스페인 및 포르투갈',
    'article.inpost.badge': '네트워크 확장',
    'article.inpost.imageCaption': '이베리아 반도 InPost 택배함',
    'article.inpost.summary': 'InPost가 성수기 일일 50만 건 배송 처리 능력으로 늘리며 전년 대비 220% 증가했습니다.',
    'article.inpost.highlight': '네트워크를 약 4,000개 택배함으로 확장, 55%가 24시간 이용 가능',
    'article.inpost.section1.title': '용량 확장',
    'article.inpost.section1.content': '2025년 네트워크 확장, 택배함의 99.38%가 주 6일 이상 접근 가능합니다.',
    'article.inpost.section2.title': '운영 개선',
    'article.inpost.section2.content': '직원 60% 증가, 88명 신규 채용, 13개 정규직 생성했습니다.',
    'article.inpost.section3.title': '서비스 커버리지',
    'article.inpost.section3.content': '스페인, 포르투갈, 도서 지역 우편번호 100% 커버리지를 달성했습니다.',
    'article.inpost.section4.title': '국제 통합',
    'article.inpost.section4.content': '7월 Sending 인수 후 유럽 6개국에서 국경 간 배송 서비스 제공합니다.',
    'article.inpost.impact.title': '성수기 대비',
    'article.inpost.impact.content': '블랙프라이데이, 크리스마스, 연후 세일 대비 용량 증대했습니다.',

    // EuroCommerce Tax Article
    'article.eurocommerce.title': 'EuroCommerce: 택배 세금은 EU 전체에 적용되어야',
    'article.eurocommerce.date': '2025년 11월 5일',
    'article.eurocommerce.location': '유럽연합',
    'article.eurocommerce.badge': '정책 개혁',
    'article.eurocommerce.imageCaption': '유럽연합 세관 및 택배 처리',
    'article.eurocommerce.summary': '유럽 소매 협회 EuroCommerce가 국제 택배에 대한 통일된 2유로 수수료 시행을 촉구하고 있습니다.',
    'article.eurocommerce.highlight': 'EU 외부 택배에 2유로, 유럽 창고에 0.50유로 수수료',
    'article.eurocommerce.section1.title': '유럽위원회 제안',
    'article.eurocommerce.section1.content': '플랫폼이 지불하는 고정 2유로 수수료, 2028년 3월 시행 예정입니다.',
    'article.eurocommerce.section2.title': '국가별 시행 우려',
    'article.eurocommerce.section2.content': '벨기에 2026년부터 2유로 수수료, 네덜란드 2028년 이전 시행 검토 중입니다.',
    'article.eurocommerce.section3.title': '분열 위험',
    'article.eurocommerce.section3.content': '국가별 접근법이 단일 시장 무결성을 훼손시킬 수 있다고 경고했습니다.',
    'article.eurocommerce.section4.title': '시장 영향',
    'article.eurocommerce.section4.content': '2024년 온라인 소비 지출이 7% 증가하여 8,190억 유로에 달했습니다.',
    'article.eurocommerce.impact.title': '조화 필요성',
    'article.eurocommerce.impact.content': '회원국 간 공정한 경쟁 환경을 위해 신속한 EU 통일 솔루션이 필수적입니다.',

    
    // Risk Sensing
    'risk.title': '최신 위험 감지',
    
    'risk.cosco.title': 'COSCO 전세 컨테이너선, 중국 항만 수수료 분쟁에 휘말려',
    'risk.cosco.description': 'COSCO 운영 컨테이너선이 미국 연계 선박을 대상으로 한 새로운 항만 수수료 적용 후 중국 기항을 건너뛰며, 국영기업에도 엄격한 집행을 보여줌.',
    'risk.cosco.date': '2025년 10월 28일',
    'risk.cosco.badge': '무역 중단',
    'risk.cosco.vessel': '선박',
    'risk.cosco.status': '상태',
    'risk.cosco.statusValue': '면제 요청 중',
    'risk.cosco.impact': '영향 수준',
    'risk.cosco.impactValue': '고위험',
    
    // COSCO Boxship Article
    'article.coscoBoxship.title': 'COSCO 전세 컨테이너선, 중국 항만 수수료 분쟁에 휘말려',
    'article.coscoBoxship.readTime': '4분 읽기',
    'article.coscoBoxship.imageCaption': '미국 연계 선박을 대상으로 한 새로운 수수료 규정 속에서 중국 항만의 컨테이너 운영',
    'article.coscoBoxship.lead': 'COSCO Shipping이 운영하는 컨테이너선이 미국 연계 선박을 대상으로 한 중국의 보복 항만 수수료의 예상치 못한 피해자가 되어, 국영기업에 대해서도 새로운 무역 제재의 복잡한 집행을 부각시켰습니다.',
    'article.coscoBoxship.section1.title': '정책 교차점에 걸린 선박',
    'article.coscoBoxship.section1.content': '2025년 4월부터 2년 계약으로 COSCO Shipping이 전세한 2,550 TEU급 Halley호는 중국 교통부가 설정한 25% 임계값을 초과하는 미국 지분을 보유한 것으로 당국이 판단한 후 중국 항만 기항을 건너뛸 수밖에 없었습니다.',
    'article.coscoBoxship.vessel.title': '선박 사양',
    'article.coscoBoxship.vessel.name': '선박명',
    'article.coscoBoxship.vessel.capacity': '적재능력',
    'article.coscoBoxship.vessel.flag': '선적국',
    'article.coscoBoxship.vessel.charter': '전세 기간',
    'article.coscoBoxship.vessel.charterValue': '2년 계약 (2025년 4월)',
    'article.coscoBoxship.section2.title': '보복 조치',
    'article.coscoBoxship.section2.content': '2025년 10월 14일 시행된 새로운 항만 수수료는 중국의 해운 부문을 대상으로 한 미국 무역대표부 관세에 대한 직접적인 보복으로 도입되었습니다. 이 규정은 중국 국영 운영업체에 대해서도 예외 없이 엄격한 집행에 대한 베이징의 의지를 보여줍니다.',
    'article.coscoBoxship.section3.title': '운영상 도전',
    'article.coscoBoxship.section3.content': '소식통에 따르면 라이베리아 선적의 이 선박은 COSCO가 당국으로부터 면제를 요청하는 동안 닝보 앞바다에서 며칠간 대기했습니다. 본토 수수료를 적용하지 않겠다고 확인한 홍콩으로 우회하는 대안 계획도 거의 진전을 보지 못했습니다.',
    'article.coscoBoxship.impact.title': '업계 영향 평가',
    'article.coscoBoxship.impact.point1': '편애 없는 무역 제재의 엄격한 집행을 보여줌',
    'article.coscoBoxship.impact.point2': '국제 해운 회사들에게 운영상 불확실성을 조성',
    'article.coscoBoxship.impact.point3': '유사한 소유 구조를 가진 다른 COSCO 전세 선박에 영향을 미칠 수 있음',
    'article.coscoBoxship.impact.point4': '해운 부문에서 미중 무역 긴장의 복잡성을 부각',
    'article.coscoBoxship.section4.title': '광범위한 시사점',
    'article.coscoBoxship.section4.content': '업계 전문가들은 이 사례가 무역 제재의 공정한 집행에 대한 중국의 의지를 나타낼 수 있으며, COSCO의 전세 선단에서 더 많은 선박에 영향을 미칠 가능성이 있다고 제안합니다. 이 사건은 해운 부문에서 미중 무역 긴장을 헤쳐나가는 것의 증가하는 복잡성을 강조합니다.',
    'risk.strikes.title': '로테르담과 안트베르펜 항만, 파업 종료 후에도 여전히 혼잡',
    'risk.strikes.location': '네덜란드 및 벨기에',
    'risk.strikes.content': '로테르담과 안트베르펜 항만에서 파업이 공식적으로 종료되었지만, 선박 적체로 인한 상당한 혼잡이 지속되어 유럽 물류 운영에 계속 영향을 미치고 있습니다.',
    'risk.strikes.impact': '영향 평가',
    'risk.strikes.ships': '대기 선박 (최대):',
    'risk.strikes.duration': '파업 기간:',
    'risk.strikes.recovery': '회복 상태:',
    'risk.strikes.badge': '항만 중단',
    
    'risk.dashboard.title': '위험 모니터링 대시보드',
    'risk.dashboard.subtitle': '실시간 물류 위험 지표',
    'risk.dashboard.congestion': '항만 혼잡',
    'risk.dashboard.volatility': '화물 운임 변동성',
    'risk.dashboard.rail': '철도 네트워크 상태',
    'risk.dashboard.regulatory': '규제 변화',
    
    // Logistics Data
    'data.title': '거시경제 지수',
    'data.diesel.title': '유럽 국가별 디젤 연료 가격',
    'data.diesel.subtitle': '국가별 리터당 월별 디젤 가격 (EUR) - 2024년 1월부터 2025년 10월까지',
    'data.diesel.insights': '주요 인사이트',
    'data.diesel.average': '• EU 평균 디젤 가격 추세: 리터당 €1.52에서 €1.63',
    'data.diesel.lowest': '• 최저 평균은 2024년 2월 리터당 €1.51',
    'data.diesel.highest': '• 최고 평균은 2025년 리터당 €1.63 달성',
    'data.diesel.change': '• 22개월 기간 동안 리터당 €0.11 전체 증가',
    
    'data.inflation.title': '유럽 국가별 인플레이션율',
    'data.inflation.subtitle': '국가별 연간 인플레이션율 (%) - 2024년 1월부터 2025년 10월까지',
    'data.inflation.update': '2025년 10월 업데이트',
    'data.inflation.current': '현재 비율:',
    'data.inflation.target': 'ECB 목표:',
    'data.inflation.monthly': '월간 변화:',
    'data.inflation.core': '핵심 인플레이션:',
    
    'data.minimumWage.title': '유럽 국가별 최저임금 2025',
    'data.minimumWage.subtitle': '국가별 실제 최저임금율 (EUR/시간) - 2025년',
    'data.minimumWage.insights': '주요 인사이트',
    'data.minimumWage.highest': '• 네덜란드가 시간당 €12.83으로 선두',
    'data.minimumWage.lowest': '• 폴란드가 시간당 €4.30으로 최저',
    'data.minimumWage.average': '• 서유럽이 동유럽보다 현저히 높음',
    'data.minimumWage.stability': '• 최고와 최저 임금 간 3배 차이',
    
    // Common
    'common.share': '공유',
    'common.back': '뒤로',
    'common.readMore': '더 읽기',
    'common.declining': '하락',
    'common.expansion': '확장',
    'common.newPolicy': '새 정책',
    'common.highRisk': '고위험',
    'common.high': '높음',
    'common.medium': '보통',
    'common.normal': '정상',
    'common.monitoring': '모니터링',
    'common.ongoing': '진행 중',
    
    // Articles
    'article.relatedArticles': '관련 기사',
    
    // Freight Rates Article
    'article.freight.title': '유럽 물류 기업들, 화물 운임 하락의 영향에 직면',
    'article.freight.date': '2024년 10월 21일',
    'article.freight.readTime': '5분 읽기',
    'article.freight.imageCaption': '화물 운임 하락 속에서 머스크 선박을 처리하는 브레머하펜 항구의 컨테이너 크레인',
    'article.freight.lead': '유럽 해운 및 물류 기업들이 컨테이너 운임이 2024년 1월 이후 최저 수준으로 떨어지면서 상당한 압박을 받고 있으며, 이는 업계 공급 과잉과 새로운 미국 무역 정책에 따른 수요 감소로 인한 것입니다.',
    'article.freight.section1.title': '시장 압박 증가',
    'article.freight.section1.content': '컨테이너 해운 시장의 과잉 공급과 글로벌 무역량 약화의 결합이 유럽 물류 공급업체들에게 완벽한 폭풍을 만들어냈습니다. 머스크와 같은 대형 운송업체들은 장기 계약으로 인해 더 나은 위치에 있지만, 소규모 운영업체들은 즉각적인 마진 압박에 직면하고 있습니다.',
    'article.freight.chart.title': '화물 운임 및 물량 동향',
    'article.freight.chart.description': '월별 화물 운임(USD) 및 컨테이너 물량(백만 TEU)',
    'article.freight.section2.title': '지역별 영향 분석',
    'article.freight.section2.content': '북유럽 항구들이 가장 큰 영향을 받고 있으며, 로테르담과 함부르크가 처리량 감소를 기록하고 있습니다. 파급 효과는 내륙 물류 네트워크로 확산되어 유럽 전역의 철도 및 트럭 운송 작업에 영향을 미치고 있습니다.',
    'article.freight.keyStats.title': '주요 시장 지표',
    'article.freight.keyStats.rateDecline': '7월 이후 운임 하락',
    'article.freight.keyStats.currentRate': '현재 평균 운임(USD)',
    'article.freight.keyStats.volumeTEU': '처리된 TEU(10월)',
    'article.freight.keyStats.waitingShips': '정박지 대기 선박',
    'article.freight.section3.title': '업계 대응',
    'article.freight.section3.content': '물류 기업들은 비용 절감 조치를 시행하고 대안적 수익원을 모색하고 있습니다. 일부는 운영 효율성을 개선하고 간접비를 줄이기 위해 디지털화 노력을 가속화하고 있습니다.',
    'article.freight.section4.title': '전망 및 시사점',
    'article.freight.section4.content': '분석가들은 현재의 침체가 2025년 1분기까지 지속될 것으로 예상하며, 회복은 글로벌 무역 정상화와 주요 운송업체들의 용량 조정에 달려 있다고 봅니다. 다양한 서비스 포트폴리오를 가진 기업들이 폭풍을 더 잘 견딜 것으로 예상됩니다.',
    
    // Train Connection Article
    'article.train.title': 'FS Logistix, 안트베르펜-밀라노 간 철도 연결 인수',
    'article.train.date': '2024년 10월 21일',
    'article.train.readTime': '4분 읽기',
    'article.train.imageCaption': '새로운 밀라노 연결을 촉진하는 안트베르펜 메인 허브의 FS Logistix 복합운송 터미널',
    'article.train.lead': '이탈리아 국영 물류 회사 FS Logistix가 전략적 안트베르펜-밀라노 철도 회랑을 성공적으로 인수하여 유럽 복합운송 능력의 중요한 확장을 표시했습니다.',
    'article.train.section1.title': '전략적 인수 세부사항',
    'article.train.section1.content': '벨기에 운영업체 Lineas로부터의 인수는 유럽 철도 인프라에 4,500만 유로의 투자를 나타냅니다. FS Logistix는 이제 Mercitalia Intermodal과의 파트너십을 통해 운영하며, 포괄적인 견인 계약 하에 Lineas 기관차를 사용합니다.',
    'article.train.route.title': '노선 사양',
    'article.train.route.description': '안트베르펜에서 밀라노까지의 완전한 여행 세부사항',
    'article.train.route.departure': '메인 허브 터미널, 벨기에',
    'article.train.route.arrival': '세그라테 터미널, 이탈리아',
    'article.train.route.terminalSize': '터미널 면적',
    'article.train.route.transitTime': '운송 시간',
    'article.train.section2.title': '환경 영향',
    'article.train.section2.content': '이 서비스는 지속 가능한 물류를 향한 중요한 단계를 나타내며, 매년 수천 대의 트럭을 유럽 고속도로에서 제거합니다. 각 열차는 52대의 트럭 적재량에 해당하는 화물을 운송할 수 있어 탄소 배출과 도로 혼잡을 크게 줄입니다.',
    'article.train.environmental.title': '지속가능성 지표',
    'article.train.environmental.trucksRemoved': '연간 제거된 트럭',
    'article.train.environmental.co2Reduction': 'CO2 배출 감소',
    'article.train.environmental.weeklyFrequency': '주간 출발',
    'article.train.section3.title': '운영 우수성',
    'article.train.section3.content': '안트베르펜 메인 허브 터미널은 8개의 700미터 선로, 3개의 갠트리 크레인, 6개의 스트래들 캐리어를 갖춘 200,000평방미터에 걸쳐 있습니다. 이 인프라는 효율적인 컨테이너 처리와 빠른 회전 시간을 지원합니다.',
    'article.train.section4.title': '미래 확장 계획',
    'article.train.section4.content': 'FS Logistix는 로마와 나폴리를 포함한 추가 이탈리아 목적지로의 연결 확장을 계획하고 있으며, 안트베르펜 허브를 통해 프랑스, 독일, 동유럽으로의 서비스 기회를 탐색하고 있습니다.',
    'article.train.partnership.title': '파트너십 구조',
    'article.train.partnership.fsRole': '서비스 운영자 및 상업적 리더십',
    'article.train.partnership.mercitaliaRole': '철도 운영 관리',
    'article.train.partnership.lineasRole': '기관차 및 견인 서비스',
    
    // Carbon Market Article
    'article.carbon.title': '건물 및 도로 운송을 위한 유럽의 새로운 탄소 시장',
    'article.carbon.date': '2024년 10월 21일',
    'article.carbon.readTime': '6분 읽기',
    'article.carbon.imageCaption': '새로운 ETS2 탄소 시장 법안을 나타내는 유럽연합 집행위원회 건물',
    'article.carbon.lead': '유럽연합의 획기적인 ETS2 시스템은 건물 및 운송 부문의 탄소 가격 책정을 혁신하여 2027년까지 세계에서 가장 포괄적인 탄소 시장을 만들 것입니다.',
    'article.carbon.section1.title': 'ETS2 프레임워크 개요',
    'article.carbon.section1.content': '새로운 배출권 거래 시스템은 원래 EU ETS에 포함되지 않은 건물, 도로 운송 및 추가 산업 부문의 연료 유통을 다룹니다. 이 확장은 1,040억 유로의 시장 기회를 나타냅니다.',
    'article.carbon.chart.title': '부문별 배출 목표',
    'article.carbon.chart.description': '부문별 현재 배출량 대 2030년 목표(백만 톤 CO2)',
    'article.carbon.chart.buildings': '건물',
    'article.carbon.chart.transport': '운송',
    'article.carbon.chart.industry': '소규모 산업',
    'article.carbon.chart.current': '현재 배출량',
    'article.carbon.chart.target2030': '2030년 목표',
    'article.carbon.section2.title': '구현 일정',
    'article.carbon.section2.content': '시스템은 2027년에 CO2 톤당 45유로의 최저 가격으로 시작됩니다. 회원국들은 녹색 전환 투자를 지원하고 취약한 가정을 에너지 가격 상승으로부터 보호하기 위한 수익을 받게 됩니다.',
    'article.carbon.keyFeatures.title': '주요 특징',
    'article.carbon.keyFeatures.startDate': '시스템 출시 연도',
    'article.carbon.keyFeatures.reductionTarget': '2030년까지 배출 감소',
    'article.carbon.keyFeatures.priceFloor': '최소 탄소 가격',
    'article.carbon.keyFeatures.marketValue': '예상 시장 가치',
    'article.carbon.section3.title': '시장 메커니즘',
    'article.carbon.section3.content': 'ETS2는 자동 가격 안정화 메커니즘과 회원국으로의 수익 재활용을 포함합니다. 시장 안정성 준비금이 과도한 가격 변동성을 방지하기 위해 배출권 공급을 관리할 것입니다.',
    'article.carbon.timeline.title': '구현 일정',
    'article.carbon.timeline.preparation': '시스템 설계 및 준비 단계',
    'article.carbon.timeline.launch': 'ETS2 운영 시작',
    'article.carbon.timeline.target': '42% 배출 감소 목표',
    'article.carbon.section4.title': '경제적 시사점',
    'article.carbon.section4.content': '탄소 시장은 청정 기술 채택을 장려하면서 녹색 투자를 위한 상당한 수익을 창출할 것입니다. 산업계는 이미 효율성 개선과 대체 연료 전략으로 전환을 준비하고 있습니다.',
    
    // AWS Outage Article
    'article.aws.title': 'AWS 장애로 글로벌 공급망 차질',
    'article.aws.date': '2024년 10월 25일',
    'article.aws.readTime': '5분 읽기',
    'article.aws.imageCaption': '공급망 운영에 영향을 미치는 글로벌 클라우드 인프라 장애',
    'article.aws.lead': '4시간 이상 지속된 주요 Amazon Web Services 장애가 유럽과 북미의 공급망 관리 시스템에 광범위한 차질을 일으켜 클라우드 인프라에 대한 중요한 의존성을 부각시켰습니다.',
    'article.aws.section1.title': '장애 영향 평가',
    'article.aws.section1.content': '이 장애는 AWS의 US-East-1 지역에 영향을 미쳐 유럽 서비스로 확산되었고, 주요 소매업체와 물류 공급업체가 사용하는 물류 플랫폼, 재고 관리 시스템, 실시간 추적 애플리케이션에 영향을 미쳤습니다.',
    'article.aws.chart.title': '지역별 서비스 복구',
    'article.aws.chart.description': '지역별 장애 중 및 후 서비스 가용성',
    'article.aws.chart.usEast': '미국 동부',
    'article.aws.chart.europe': '유럽',
    'article.aws.chart.asiaPacific': '아시아 태평양',
    'article.aws.chart.affected': '최대 영향',
    'article.aws.chart.recovered': '복구율',
    'article.aws.section2.title': '공급망 차질',
    'article.aws.section2.content': '전자상거래 플랫폼은 주문 처리 지연을 경험했고, 물류 회사들은 배송 추적에 대한 실시간 가시성을 잃었습니다. 창고 관리 시스템이 오프라인이 되어 재고 불일치와 이행 지연이 발생했습니다.',
    'article.aws.sectorChart.title': '부문별 영향 분포',
    'article.aws.sectorChart.description': '산업 부문별 영향받은 서비스 비율',
    'article.aws.sectors.ecommerce': '전자상거래',
    'article.aws.sectors.logistics': '물류',
    'article.aws.sectors.manufacturing': '제조업',
    'article.aws.sectors.retail': '소매업',
    'article.aws.keyStats.title': '장애 통계',
    'article.aws.keyStats.duration': '총 장애 지속 시간',
    'article.aws.keyStats.estimatedLoss': '예상 경제적 영향',
    'article.aws.keyStats.affectedServices': '영향받은 서비스',
    'article.aws.keyStats.recoveryRate': '완전 복구 달성',
    'article.aws.section3.title': '복구 작업',
    'article.aws.section3.content': 'AWS는 중요한 서비스를 복원하기 위한 비상 프로토콜을 구현하여 공급망 및 물류 애플리케이션을 우선시했습니다. 회사는 정기적인 업데이트를 제공하고 영향받은 고객에게 서비스 크레딧을 제공했습니다.',
    'article.aws.timeline.title': '사건 타임라인',
    'article.aws.timeline.start': '초기 서비스 저하 감지',
    'article.aws.timeline.peak': '모든 지역에서 최대 영향',
    'article.aws.timeline.resolution': '완전한 서비스 복원 완료',
    'article.aws.section4.title': '업계 대응',
    'article.aws.section4.content': '이 사건은 공급망 관리자들이 클라우드 의존성과 재해 복구 전략을 재평가하도록 촉발했습니다. 많은 기업들이 이제 단일 장애점을 줄이기 위해 멀티클라우드 아키텍처를 고려하고 있습니다.',
    'article.aws.recommendations.title': '위험 완화 전략',
    'article.aws.recommendations.multiCloud': '멀티클라우드 아키텍처 구현',
    'article.aws.recommendations.backupSystems': '오프라인 백업 시스템 유지',
    'article.aws.recommendations.monitoring': '향상된 모니터링 및 알림',
    'article.aws.recommendations.contingency': '포괄적인 비상 계획 개발',
    
    // Port Strikes Article
    'article.strikes.title': '로테르담과 안트베르펜 항만 파업 완화되지만 혼잡 지속',
    'article.strikes.date': '2024년 10월 22일',
    'article.strikes.readTime': '4분 읽기',
    'article.strikes.imageCaption': '최근 파업 중 로테르담 항구 밖에서 대기 중인 컨테이너선들',
    'article.strikes.lead': '유럽의 가장 바쁜 두 항구에서 노동 쟁의가 공식적으로 종료되었지만, 여파는 선박 적체와 지연된 화물 처리로 물류 운영에 계속 영향을 미치고 있습니다.',
    'article.strikes.section1.title': '파업 해결',
    'article.strikes.section1.content': '로테르담과 안트베르펜 모두 파업 노동자들과 합의에 도달했지만, 회복 과정은 몇 주가 걸릴 것으로 예상됩니다. 항만 당국은 대기 중인 선박의 적체를 해소하기 위해 24시간 작업하고 있습니다.',
    'article.strikes.chart.title': '선박 혼잡 타임라인',
    'article.strikes.chart.description': '정박지 할당을 기다리는 선박 수',
    'article.strikes.chart.oct5': '10월 5일',
    'article.strikes.chart.oct8': '10월 8일',
    'article.strikes.chart.oct10': '10월 10일',
    'article.strikes.chart.oct15': '10월 15일',
    'article.strikes.chart.oct18': '10월 18일',
    'article.strikes.chart.oct22': '10월 22일',
    'article.strikes.section2.title': '항구별 문제',
    'article.strikes.section2.content': '로테르담은 임금 분쟁으로 인한 라싱 크루 파업에 직면했고, 안트베르펜은 연금 개혁과 관련된 도선사 파업을 다뤘습니다. 이러한 분쟁의 다른 성격은 별도의 협상 과정을 필요로 했습니다.',
    'article.strikes.rotterdam.issue': '파업 문제',
    'article.strikes.rotterdam.issueValue': '임금 분쟁',
    'article.strikes.rotterdam.duration': '파업 기간',
    'article.strikes.rotterdam.peakShips': '최대 혼잡',
    'article.strikes.antwerp.issue': '파업 문제',
    'article.strikes.antwerp.issueValue': '연금 개혁',
    'article.strikes.antwerp.duration': '파업 기간',
    'article.strikes.antwerp.peakShips': '최대 혼잡',
    'article.strikes.section3.title': '경제적 영향',
    'article.strikes.section3.content': '결합된 파업은 상당한 경제적 손실을 초래했으며, 지연된 배송이 유럽 전역의 공급망에 영향을 미쳤습니다. 소매업체와 제조업체는 재고 부족과 증가된 물류 비용에 직면했습니다.',
    'article.strikes.economicImpact.title': '재정적 영향 평가',
    'article.strikes.economicImpact.dailyLoss': '일일 경제적 손실',
    'article.strikes.economicImpact.affectedTEU': '영향받은 TEU',
    'article.strikes.economicImpact.delayIncrease': '평균 지연 증가',
    'article.strikes.economicImpact.avgDelay': '평균 처리 지연',
    'article.strikes.section4.title': '회복 진행',
    'article.strikes.section4.content': '항만 운영이 점진적으로 정상으로 돌아가고 있으며, 시간에 민감한 화물과 부패하기 쉬운 상품에 우선순위를 두고 있습니다. 회복을 가속화하기 위해 추가 직원과 연장된 운영 시간이 구현되고 있습니다.',
    'article.strikes.recovery.title': '현재 회복 상태',
    'article.strikes.recovery.rotterdam': '로테르담 운영',
    'article.strikes.recovery.rotterdamStatus': '완전 운영',
    'article.strikes.recovery.antwerp': '안트베르펜 운영',
    'article.strikes.recovery.antwerpStatus': '회복 중',
    'article.strikes.recovery.normalOps': '정상 복귀',
    
    // Source Section
    'article.source.title': '원본 출처',
    'article.source.description': '출처 출판물의 원본 기사를 읽어보세요:',
    'article.source.readOriginal': '원본 기사 읽기',
    'article.source.publisher': '발행처',
    
    // Logistics Partners Insights
    // Market Insights
    'market.title': '시장 인사이트',
    'market.subtitle': '월간 물류 인사이트 보고서 - 2025년 11월',
    'market.executiveSummary.title': '요약',
    'market.roadFreight.title': '유럽 도로 화물 시장',
    'market.roadFreight.summary': 'Q2 최고치 이후 요금이 높은 수준에서 안정되었으며, 현물 및 계약 요금이 수렴하고 있습니다. 그러나 유럽 전역에서 426,000개 이상의 운전자 부족으로 인해 용량이 여전히 심각하게 부족합니다.',
    'market.roadFreight.keyPoint1': '운전자 부족: 426,000+ 미충원 직책',
    'market.roadFreight.keyPoint2': '요금 지수 132.2-134.06에서 안정화',
    'market.roadFreight.keyPoint3': 'EU 신규 트럭 등록 15.4% 감소',
    'market.oceanFreight.title': '글로벌 해상 화물 시장',
    'market.oceanFreight.summary': '관세 불확실성을 완화하기 위한 선적 물량이 감소하면서 수요가 연화되어 해상 화물 요금이 2025년 상반기 최고치에서 하락하고 있습니다.',
    'market.oceanFreight.keyPoint1': 'SCFI 종합지수 1,550.70포인트 (10월 31일)',
    'market.oceanFreight.keyPoint2': '9월 글로벌 신뢰도 65.2%',
    'market.oceanFreight.keyPoint3': '지연 선박 평균 지연 시간: 4.88일',
    'market.airFreight.title': '글로벌 항공 화물 시장',
    'market.airFreight.summary': '항공 화물 시장은 여름 승객 스케줄의 혜택을 받는 유럽 루트를 중심으로 용량 개선을 배경으로 안정적인 수요를 보이고 있습니다.',
    'market.airFreight.keyPoint1': '9월 CTK 전년 대비 2.9% 증가',
    'market.airFreight.keyPoint2': '용량(ACTK) 3.0% 병행 성장',
    'market.airFreight.keyPoint3': 'ICS2 및 SAF 의무로 비용 증가',
    'market.realEstate.title': '유럽 부동산 및 창고업',
    'market.realEstate.summary': '유럽 물류 부동산 시장은 취약한 경제 회복과 맞물려 둔화를 경험하고 있습니다. 고품질 임차인을 보유한 우수 입지에 대한 투자자 관심은 여전하지만, 투기적 개발은 감소했습니다.',
    'market.realEstate.keyPoint1': '창고 점유율 2014년 이후 최저 수준',
    'market.realEstate.keyPoint2': '독일 프라임 수익률 4.50%에서 안정',
    'market.realEstate.keyPoint3': '스페인이 아웃퍼포머로서 회복력 보여',
    'market.recommendations.title': '화주를 위한 즉시 조치',
    'market.recommendations.point1': '안정적이고 현물 용량을 위한 운송업체 믹스 다양화',
    'market.recommendations.point2': '사전 인출 및 드롭 트레일러 구현',
    'market.recommendations.point3': '비수기 시간대로 슬롯 유연성 향상',
    'market.recommendations.point4': '아시아-유럽 배송에 7-12일 버퍼 추가',

    'partners.title': '물류 파트너 인사이트',
    'partners.kuehne.title': '유럽은 전략적 회복력을 위해 항만 인프라에 투자해야 한다',
    'partners.kuehne.subtitle': '머스크 CEO와 APMT 리더십이 통합 유럽 항만 전략 촉구',
    'partners.kuehne.badge': '2025년 11월',
    'partners.kuehne.keyTakeaways': '핵심 메시지',
    'partners.kuehne.additionalInsights': '전략적 시사점',
    'partners.kuehne.takeaway1': '유럽은 혼잡 완화와 전략적 회복력 구축을 위해 항만 인프라에 진지한 투자가 필요',
    'partners.kuehne.takeaway2': '대륙 전체에 항만 용량이 부족하여 인프라 개발에 통합된 접근법 필요',
    'partners.kuehne.takeaway3': '투자는 무역 성장, 효율성 요구, 변화하는 지정학적 현실과 보조를 맞춰야 함',
    'partners.kuehne.takeaway4': '주요 유럽 항만에서의 중국 영향력이 EU 주권에 대한 전략적 우려 제기',
    'partners.kuehne.takeaway5': '북유럽 주요 항만들이 2025년 내내 지속적인 혼잡에 직면',
    'partners.kuehne.takeaway6': '향후 몇 년간 예상되는 물량 줼나미가 기존 항만 용량에 부담 가중',
    'partners.kuehne.takeaway7': '레거시 인프라는 더 크고 많아진 차세대 선박을 위해 설계되지 않음',
    'partners.kuehne.takeaway8': '항만은 공항, 도로, 상수도 시스템과 같은 중요 인프라로 간주되어야 함',
    'partners.kuehne.source': 'Kuehne+Nagel 해상 인텔리전스',
    'partners.kuehne.sourceDescription': '유럽 항만 인프라 및 용량 과제에 대한 전략적 분석',
    'partners.kuehne.viewTracker': '전체 분석 읽기',
    
    // Footer
    'footer.title': 'LogiSense Portal',
    'footer.subtitle': '유럽 물류 뉴스 및 위험 인텔리전스',
    'footer.updated': '최종 업데이트: 2025년 11월 7일 | 데이터 출처: IRU, Ti Insight, Shanghai Shipping Exchange, Drewry Maritime, Sea-Intelligence, IATA, 유럽위원회, CBRE, Cushman & Wakefield',
    
    // Chart labels
    'chart.rate': '비율 (USD)',
    'chart.inflationRate': '인플레이션율',
    'chart.pricePerLiter': '리터당 가격',
    
    // Countries
    'country.germany': '독일',
    'country.france': '프랑스',
    'country.netherlands': '네덜란드',
    'country.belgium': '벨기에',
    'country.italy': '이탈리아',
    
    // Months
    'month.jan': '1월',
    'month.feb': '2월',
    'month.mar': '3월',
    'month.apr': '4월',
    'month.may': '5월',
    'month.jun': '6월',
    'month.jul': '7월',
    'month.aug': '8월',
    'month.sep': '9월',
    'month.oct': '10월',
    'month.nov': '11월',
    'month.dec': '12월',
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};